import { useEffect, useState, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useSecurity } from "@/security/ArmadilloProvider";
import { MapPin, Clock, ArrowLeft, CheckCircle2, Upload, MessageSquare, Users, FileText, XCircle, AlertCircle, Shield, User, Trash2, Edit, Timer , Image as ImageIcon, Link as LinkIcon, X, ChevronLeft, ChevronRight, Award, TrendingUp, Target, Calendar, MessageCircle, Loader2, Check, Mic, Trophy } from "lucide-react";
import { SafeProofLink } from "@/components/SafeProofLink";
import { SafeRichText } from "@/components/SafeRichText";
import { buildAuthUrl } from "@/lib/authFlow";
import { bountyApi, storageApi, wallApi } from "@/services/api";
import { supabase } from "@/integrations/supabase/client";
import { ROUTES, STORAGE_CONFIG, SUCCESS_MESSAGES } from "@/config/constants";
import type { BountyRow, ClaimRequest, HunterReputation, ClaimPermissionResult } from "@/types";
import { extractStoragePathFromUrl, isExpired, getActualBountyStatus, canClaimBounty, parsePhotoSetRequirement } from "@/lib/utils";
import { useLocalization } from "@/hooks/useLocalization";
import { BountyChat } from "@/components/BountyChat";
import { countryToFlag, COUNTRY_NAMES } from "@/lib/countryFlags";
import { BountyExtensionDialog } from "@/components/BountyExtensionDialog";
import { WallPublishDialog } from "@/components/WallPublishDialog";
import { AudioRecorder } from "@/components/AudioRecorder";
import { AudioPlayer } from "@/components/AudioPlayer";
import { validateProofRequirements, formatValidationErrors, type ProofRequirement, type ValidationResult } from "@/utils/proofValidation";
import { getServerNow } from "@/lib/serverTime";
import {
  normalizeProofType,
  isFileProofType,
  isImageProofType,
  MAX_VIDEO_FILES,
  MAX_VIDEO_DURATION_SECONDS,
} from "@/lib/proofTypes";
import { ProofRejectionDialog } from "@/components/ProofRejectionDialog";

interface Bounty extends BountyRow {}

const Detail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [b, setB] = useState<Bounty | null>(null);
  const [loading, setLoading] = useState(true);
  const [proofText, setProofText] = useState("");
  const [files, setFiles] = useState<File[]>([]); // Changed to array for multiple files
  const [filePreviews, setFilePreviews] = useState<string[]>([]); // Preview URLs
  // Photo-set proof slots (one labeled slot per required photo)
  const [photoSetFiles, setPhotoSetFiles] = useState<(File | null)[]>([]);
  const [photoSetPreviews, setPhotoSetPreviews] = useState<(string | null)[]>([]);
  const [busy, setBusy] = useState(false);
  const [signedProofUrl, setSignedProofUrl] = useState<string | null>(null);
  const [signedProofUrls, setSignedProofUrls] = useState<string[]>([]);
  const [proofViewerUrl, setProofViewerUrl] = useState<string | null>(null);
  const [proofViewerType, setProofViewerType] = useState<string>("");
  const [claimProofUrls, setClaimProofUrls] = useState<Record<string, string[]>>({});
  const [rejectDialogClaimId, setRejectDialogClaimId] = useState<string | null>(null);
  const [proofFilesValid, setProofFilesValid] = useState(true);
  
  // New claim workflow state
  const [showClaimForm, setShowClaimForm] = useState(false);
  const [claimMessage, setClaimMessage] = useState("");
  const [claimRequests, setClaimRequests] = useState<ClaimRequest[]>([]);
  const [loadingClaims, setLoadingClaims] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [selectedChatHunter, setSelectedChatHunter] = useState<string>("");
  const [claimPermission, setClaimPermission] = useState<ClaimPermissionResult | null>(null);
  const [hunterReputations, setHunterReputations] = useState<Record<string, HunterReputation>>({});

  // Bounty Wall state (publish dialog + per-claim published badges)
  const [wallDialogClaim, setWallDialogClaim] = useState<{ id: string; proof_url: string | null } | null>(null);
  const [wallEntriesByClaim, setWallEntriesByClaim] = useState<Record<string, { slug: string }>>({});
  
  // Track bounty owner balance status
  const [bountyBalanceStatus, setBountyBalanceStatus] = useState<{
    sufficient: boolean;
    reason?: string;
    current_balance?: number;
    hunters_can_pay?: number;
  } | null>(null);
  
  // Signed URLs for reference photos
  const [signedReferenceUrls, setSignedReferenceUrls] = useState<string[]>([]);
  // Fallback URLs: funded listings store private funding-draft-assets paths,
  // legacy listings store public bounty-reference-photos paths.
  const [photoFallbackUrls, setPhotoFallbackUrls] = useState<Record<number, string>>({});
  
  // Extension state
  const [showExtensionDialog, setShowExtensionDialog] = useState(false);
  
  // Delete confirmation state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  
  // Photo lightbox state
  const [photoLightbox, setPhotoLightbox] = useState<{ isOpen: boolean; photos: string[]; currentIndex: number }>({ 
    isOpen: false, 
    photos: [], 
    currentIndex: 0 
  });
  
  // Countdown timer state
  const [timeUntilDeletion, setTimeUntilDeletion] = useState<string>("");
  const [daysUntilDeletion, setDaysUntilDeletion] = useState<number>(0);
  
  // Proof validation state
  const [validationStatus, setValidationStatus] = useState<'idle' | 'validating' | 'valid' | 'invalid'>('idle');
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [requirementChecks, setRequirementChecks] = useState<Record<string, boolean>>({});
  
  const { formatDistance } = useLocalization();
  
  // PHOENIX: Armadillo Security integration
  const { validateAction } = useSecurity();
  
  // PHOENIX: Use refs to avoid stale closures in realtime subscription
  const loadClaimRequestsRef = useRef<() => Promise<void>>(() => Promise.resolve());
  const loadRef = useRef<() => Promise<void>>(() => Promise.resolve());

  // Proof validation function
  const validateProof = async () => {
    const requirements = (b as any).proof_requirements || [];
    if (requirements.length === 0) {
      setValidationStatus('idle');
      setValidationErrors([]);
      setRequirementChecks({});
      return true;
    }

    // Photo-set proofs validate against their labeled slots instead of the generic list
    const photoSet = parsePhotoSetRequirement(requirements);
    const currentFiles = photoSet
      ? photoSetFiles.filter((f): f is File => f !== null)
      : files;

    setValidationStatus('validating');
    
    // Convert string requirements to ProofRequirement objects
    const proofReqs: ProofRequirement[] = requirements.map((req: string) => {
      const reqLower = req.toLowerCase();
      const type = reqLower.includes('timestamp') ? 'timestamp' :
                  reqLower.includes('gps') ? 'gps' :
                  reqLower.includes('color') ? 'color_photo' :
                  reqLower.includes('before and after') || reqLower.includes('multiple angle') ? 'multiple_photos' :
                  reqLower.includes('video') ? 'video_proof' :
                  reqLower.includes('audio') ? 'audio_recording' :
                  'custom';
      return { type, label: req, required: true };
    });

    const result: ValidationResult = await validateProofRequirements(currentFiles, proofReqs);
    
    // Update requirement checks for UI display
    const checks: Record<string, boolean> = {};
    for (const req of requirements) {
      const reqLower = req.toLowerCase();
      if (reqLower.includes('timestamp')) checks['timestamp'] = result.metadata.hasTimestamp;
      else if (reqLower.includes('gps')) checks['gps'] = result.metadata.hasGPS;
      else if (reqLower.includes('color')) checks['color_photo'] = result.metadata.isColor;
      else if (reqLower.includes('before and after') || reqLower.includes('multiple angle')) checks['multiple_photos'] = result.metadata.hasMultiplePhotos;
      else if (reqLower.includes('video')) checks['video_proof'] = result.metadata.hasVideo;
      else if (reqLower.includes('audio')) checks['audio_recording'] = result.metadata.hasAudio;
      else checks[req] = true; // Custom/info-only requirements always pass (no automated check)
    }
    setRequirementChecks(checks);

    if (!result.valid) {
      setValidationStatus('invalid');
      setValidationErrors(result.errors);
      return false;
    }

    setValidationStatus('valid');
    setValidationErrors([]);
    return true;
  };

  // Photo lightbox functions
  const openLightbox = async (photos: string[], index: number) => {
    // For public bucket URLs (bounty-reference-photos), no need to regenerate
    // For private bucket URLs (proofs), regenerate to ensure they're fresh
    const freshUrls: string[] = [];
    
    for (const url of photos) {
      if (url.includes('/bounty-reference-photos/')) {
        // Public bucket - use as-is
        freshUrls.push(url);
      } else if (url.startsWith('http') && url.includes('supabase.co/storage')) {
        // Private bucket - regenerate signed URL
        const { data, error } = await storageApi.getSignedProofUrl(url);
        if (!error && data) {
          freshUrls.push(data);
        } else {
          // If regeneration fails, use original URL
          freshUrls.push(url);
        }
      } else {
        // Other URLs
        freshUrls.push(url);
      }
    }
    
    setPhotoLightbox({ isOpen: true, photos: freshUrls, currentIndex: index });
    document.body.style.overflow = 'hidden'; // Prevent background scrolling
  };

  const closeLightbox = () => {
    setPhotoLightbox(prev => ({ ...prev, isOpen: false }));
    document.body.style.overflow = ''; // Restore scrolling
  };

  const navigateLightbox = (direction: 'prev' | 'next') => {
    setPhotoLightbox(prev => {
      const newIndex = direction === 'prev'
        ? (prev.currentIndex - 1 + prev.photos.length) % prev.photos.length
        : (prev.currentIndex + 1) % prev.photos.length;
      return { ...prev, currentIndex: newIndex };
    });
  };

  // Close lightbox on escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!photoLightbox.isOpen) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') navigateLightbox('prev');
      if (e.key === 'ArrowRight') navigateLightbox('next');
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [photoLightbox.isOpen]);

  // Calculate deletion countdown for expired bounties - PHOENIX: Check expiration for ALL statuses
  useEffect(() => {
    if (!b) return;
    
    const isExpiredBounty = isExpired(b.expires_at); // Check expiration regardless of status
    const isCancelledOrCompleted = b.status === "cancelled" || b.status === "completed";
    
    if (isExpiredBounty || isCancelledOrCompleted) {
      const updateCountdown = () => {
        // Calculate 7 days from expiry/completion/cancellation
        const deletionDate = isExpiredBounty 
          ? new Date(b.expires_at).getTime() + (7 * 24 * 60 * 60 * 1000)
          : new Date(b.updated_at).getTime() + (7 * 24 * 60 * 60 * 1000);
        
        // PHOENIX: Use SERVER time for consistent countdown across all devices
        const now = getServerNow();
        const timeLeft = deletionDate - now;
        
        if (timeLeft <= 0) {
          setTimeUntilDeletion("Scheduled for deletion");
          setDaysUntilDeletion(0);
          return;
        }
        
        const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24));
        const hours = Math.floor((timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
        
        setDaysUntilDeletion(days);
        
        if (days > 0) {
          setTimeUntilDeletion(`${days}d ${hours}h ${minutes}m`);
        } else {
          setTimeUntilDeletion(`${hours}h ${minutes}m`);
        }
      };
      
      updateCountdown();
      const interval = setInterval(updateCountdown, 60000); // Update every minute
      
      return () => clearInterval(interval);
    }
  }, [b]);

  // Cancel bounty handler
  // Goes through cancel_bounty rather than deleting the row, so the escrowed
  // reward is returned to the poster instead of being silently forfeited.
  const handleDeleteBounty = async () => {
    if (!b || !user) return;
    
    try {
      const { data, error } = await bountyApi.cancelBounty(b.id, "Cancelled by poster");
      
      if (error) throw error;
      
      if (!data || data.success === false) {
        toast.error(data?.error || "Failed to cancel bounty");
        return;
      }
      
      const refundCents = data.refund_cents ?? 0;
      toast.success(
        refundCents > 0
          ? `Bounty cancelled. $${(refundCents / 100).toFixed(2)} refunded to your wallet.`
          : "Bounty cancelled."
      );
      nav("/");
    } catch (error: any) {
      toast.error(error.message || "Failed to cancel bounty");
    }
  };

  // Edit bounty handler
  const handleEditBounty = () => {
    nav(`/edit/${b?.id}`);
  };

  const load = async () => {
    if (!id) return;
    const { data, error } = await bountyApi.getBountyById(id);
    if (error) {
      toast.error("Failed to load bounty");
      console.error(error);
    }
    setB(data);
    setLoading(false);
  };

  const handleExtensionSuccess = async () => {
    // Reload bounty to show new expiration time
    await load();
  };

  const loadClaimRequests = async () => {
    if (!id) return;
    setLoadingClaims(true);
    const { data, error } = await bountyApi.getBountyClaims(id);
    if (!error && data) {
      setClaimRequests(data);
      
      // PHOENIX: Always reload reputation to ensure real-time stats updates
      const reputations: Record<string, HunterReputation> = {};
      for (const claim of data) {
        if (claim.hunter_id) {
          // Remove the !reputations[claim.hunter_id] check to force reload
          const { data: repData } = await bountyApi.getHunterReputation(claim.hunter_id);
          if (repData) {
            reputations[claim.hunter_id] = repData;
          }
        }
      }
      setHunterReputations(reputations);
    }
    setLoadingClaims(false);
  };

  const removeFile = (index: number) => {
    const preview = filePreviews[index];
    if (preview?.startsWith('blob:')) URL.revokeObjectURL(preview);
    setFiles((prev) => prev.filter((_, i) => i !== index));
    setFilePreviews((prev) => prev.filter((_, i) => i !== index));
  };

  // Photo-set slot handlers (one labeled slot per required photo)
  const handlePhotoSetSlot = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Photo set slots require image files');
      return;
    }
    setPhotoSetFiles((prev) => {
      const next = [...prev];
      next[index] = file;
      return next;
    });
    setPhotoSetPreviews((prev) => {
      const next = [...prev];
      if (next[index]?.startsWith('blob:')) URL.revokeObjectURL(next[index] as string);
      next[index] = URL.createObjectURL(file);
      return next;
    });
  };

  const removePhotoSetSlot = (index: number) => {
    setPhotoSetPreviews((prev) => {
      const next = [...prev];
      if (next[index]?.startsWith('blob:')) URL.revokeObjectURL(next[index] as string);
      next[index] = null;
      return next;
    });
    setPhotoSetFiles((prev) => {
      const next = [...prev];
      next[index] = null;
      return next;
    });
  };

  // (Re)initialize photo-set slots whenever the bounty changes
  const photoSet = parsePhotoSetRequirement((b as any)?.proof_requirements);
  useEffect(() => {
    if (photoSet && photoSetFiles.length !== photoSet.labels.length) {
      setPhotoSetPreviews((prev) => {
        prev.forEach((url) => { if (url?.startsWith('blob:')) URL.revokeObjectURL(url); });
        return Array(photoSet.labels.length).fill(null);
      });
      setPhotoSetFiles(Array(photoSet.labels.length).fill(null));
    }
  }, [b?.id, photoSet?.labels.length]);

  const validateVideoDuration = (file: File): Promise<boolean> =>
    new Promise((resolve) => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = () => {
        URL.revokeObjectURL(video.src);
        resolve(video.duration <= MAX_VIDEO_DURATION_SECONDS);
      };
      video.onerror = () => resolve(false);
      video.src = URL.createObjectURL(file);
    });

  // Handle file selection and generate previews
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    let selectedFiles = Array.from(e.target.files || []);
    const proofType = b ? normalizeProofType(b.proof_type) : '';

    if (proofType === 'video') {
      if (selectedFiles.length > MAX_VIDEO_FILES) {
        toast.error(`Maximum ${MAX_VIDEO_FILES} videos allowed`);
        selectedFiles = selectedFiles.slice(0, MAX_VIDEO_FILES);
      }
      let allVideosValid = selectedFiles.length > 0;
      for (const file of selectedFiles) {
        const ext = file.name.split('.').pop()?.toLowerCase();
        const isVideo =
          file.type.startsWith('video/') ||
          ['mp4', 'webm', 'mov', 'avi', 'mkv'].includes(ext || '');
        if (!isVideo) {
          toast.error(`${file.name} is not a video file`);
          setProofFilesValid(false);
          return;
        }
        const ok = await validateVideoDuration(file);
        if (!ok) {
          toast.error(`Each video must be ${MAX_VIDEO_DURATION_SECONDS} seconds or less`);
          allVideosValid = false;
        }
      }
      setProofFilesValid(allVideosValid);
      if (!allVideosValid) {
        setFiles(selectedFiles);
        setFilePreviews([]);
        return;
      }
    } else {
      setProofFilesValid(true);
    }

    setFiles(selectedFiles);
    
    filePreviews.forEach((url) => {
      if (url.startsWith('blob:')) URL.revokeObjectURL(url);
    });
    
    const previews: string[] = [];
    selectedFiles.forEach((file) => {
      if (file.type.startsWith('image/')) {
        previews.push(URL.createObjectURL(file));
      } else if (file.type.startsWith('video/')) {
        previews.push(URL.createObjectURL(file));
      } else if (file.type === 'application/pdf') {
        previews.push('pdf');
      } else {
        previews.push('document');
      }
    });
    
    setFilePreviews(previews);
  };

  useEffect(() => { 
    load(); 
    loadClaimRequests();
  }, [id]);

  // PHOENIX: Real-time subscription for claim_requests
  useEffect(() => {
    if (!id) return;
    
    console.log('🔄 Setting up realtime subscription for claim_requests...');
    
    const channel = supabase
      .channel(`claim-requests-${id}`)
      .on(
        'postgres_changes',
        {
          event: '*', // Listen to INSERT, UPDATE, DELETE
          schema: 'public',
          table: 'claim_requests',
          filter: `bounty_id=eq.${id}`
        },
        (payload) => {
          console.log('🔔 Claim request changed:', payload.eventType, payload.new);
          
          // PHOENIX: Always reload on real-time changes (removed stale closure bug)
          // The loadingClaims state was causing this to never trigger after first load
          loadClaimRequests();
          load();
          
          // Show toast notification for important changes
          if (payload.eventType === 'INSERT') {
            toast.info('New claim request received!', {
              description: 'A hunter wants to work on this bounty.'
            });
          } else if (payload.eventType === 'UPDATE') {
            const newStatus = (payload.new as any)?.status;
            const newProofStatus = (payload.new as any)?.proof_status;
            
            if (newStatus === 'accepted' && payload.old?.status === 'pending') {
              toast.success('Claim request accepted!', {
                description: 'Hunter can now submit proof.'
              });
            } else if (newProofStatus === 'approved') {
              toast.success('Proof approved! Reward paid to hunter.', {
                description: 'Payment has been transferred.'
              });
            }
          }
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          console.log('✅ Realtime subscription active for claim_requests');
        } else if (status === 'CHANNEL_ERROR') {
          console.error('❌ Realtime subscription error');
        }
      });
    
    // Cleanup subscription on unmount
    return () => {
      console.log('🧹 Cleaning up realtime subscription');
      supabase.removeChannel(channel);
    };
  }, [id]);

  // Signed URLs per claim (owner + hunter proof display)
  useEffect(() => {
    if (!b) return;

    const loadClaimProofUrls = async () => {
      const map: Record<string, string[]> = {};
      for (const claim of claimRequests) {
        if (!claim.proof_url) continue;
        const urls = claim.proof_url.split('||').filter((u) => u.trim());
        const signed: string[] = [];
        for (const url of urls) {
          const { data, error } = await storageApi.getSignedProofUrl(url);
          signed.push(!error && data ? data : url);
        }
        map[claim.id] = signed;
      }
      setClaimProofUrls(map);

      const userClaim = claimRequests.find((c) => c.hunter_id === user?.id);
      const hunterUrls = userClaim?.id ? map[userClaim.id] || [] : [];
      setSignedProofUrls(hunterUrls);
      setSignedProofUrl(hunterUrls[0] ?? null);
    };

    loadClaimProofUrls();
  }, [b?.id, b?.proof_type, claimRequests, user?.id]);
  
  // Load wall entry status for this bounty (published badge + publish-later prefill)
  useEffect(() => {
    if (!b?.id) return;
    let cancelled = false;
    wallApi.getWallEntryByBountyId(b.id).then(({ data }) => {
      if (cancelled || !data) return;
      setWallEntriesByClaim({ [data.claim_id]: { slug: data.slug } });
    });
    return () => { cancelled = true; };
  }, [b?.id]);

  // Load signed URLs for reference photos
  useEffect(() => {
    if (!b) return; // Guard against null b
    
    const loadReferenceUrls = async () => {
      const refPhotos = (b as any).reference_photos;
      if (!refPhotos || refPhotos.length === 0) {
        setSignedReferenceUrls([]);
        return;
      }
      
      const urls: string[] = [];
      const fallbacks: Record<number, string> = {};
      for (let index = 0; index < refPhotos.length; index++) {
        const url = refPhotos[index] as string;
        if (url.startsWith('http')) {
          // Already a full URL
          urls.push(url);
        } else {
          // Legacy reference photos live in the public bounty-reference-photos
          // bucket; funded listings store paths in the funding-draft-assets
          // bucket. Try the legacy public URL first and fall back to the
          // funded bucket when the image fails to load.
          const { supabase } = await import('@/integrations/supabase/client');
          const { data: legacyUrl } = supabase.storage
            .from('bounty-reference-photos')
            .getPublicUrl(url);
          const { data: fundedUrl } = supabase.storage
            .from('funding-draft-assets')
            .getPublicUrl(url);
          
          urls.push(legacyUrl?.publicUrl || url);
          if (fundedUrl?.publicUrl) fallbacks[index] = fundedUrl.publicUrl;
        }
      }
      
      setSignedReferenceUrls(urls);
      setPhotoFallbackUrls(fallbacks);
    };
    
    loadReferenceUrls();
  }, [b?.id, b ? (b as any).reference_photos : null]);

  if (loading) return <div className="min-h-screen"><Header /><main className="container max-w-2xl py-6"><Skeleton className="h-96" /></main></div>;
  if (!b) return <div className="min-h-screen"><Header /><main className="container max-w-2xl py-6"><p>Bounty not found.</p></main></div>;

  const proofType = normalizeProofType(b.proof_type);
  const maxResubmissions = (b as Bounty & { max_resubmissions?: number }).max_resubmissions ?? 3;

  const isRequester = user?.id === b.requester_id;
  const isHunter = user?.id === b.claimed_by && !isExpired(b.expires_at); // PHOENIX: Expired overrides hunter status
  
  // Check claim request statuses for current user (Phoenix Architecture: derive UI state from data)
  const userPendingClaim = user && claimRequests.find(c => c.hunter_id === user.id && c.status === "pending");
  const userAcceptedClaim = user && claimRequests.find(c => c.hunter_id === user.id && c.status === "accepted");
  const userRejectedClaim = user && claimRequests.find(c => c.hunter_id === user.id && c.status === "rejected");
  
  // Debug logging (Phoenix Architecture: trace data flow)
  const hasAcceptedClaim = !!userAcceptedClaim;
  const isAcceptedHunter = (isHunter || hasAcceptedClaim) && !isExpired(b.expires_at); // PHOENIX: Expired overrides all
  const reward = (b.reward_cents / 100).toFixed(2);
  
  // PHOENIX: Check if bounty is FULLY claimed (all slots filled AND proofs approved)
  const approvedCount = claimRequests.filter(c => c.proof_status === 'approved').length;
  const acceptedCount = claimRequests.filter(c => c.status === 'accepted').length;
  const slotsRemaining = Math.max(0, (b.max_claimers || 1) - acceptedCount);
  const multiClaimerOpen =
    (b.max_claimers || 1) > 1 &&
    slotsRemaining > 0 &&
    !isExpired(b.expires_at) &&
    b.status !== "completed";

  const openProofDocument = async (url: string) => {
    let freshUrl = url;
    if (url.includes("supabase.co") || !url.startsWith("http")) {
      const { data, error } = await storageApi.getSignedProofUrl(url);
      if (!error && data) freshUrl = data;
    }
    window.open(freshUrl, "_blank", "noopener,noreferrer");
  };

  const canSubmitProofForm =
    !busy &&
    proofFilesValid &&
    (proofType !== "video" || files.length === 0 || proofFilesValid);

  const isFullyClaimed = b.max_claimers && approvedCount >= b.max_claimers;
  
  // CRITICAL FIX: Bounty should NOT show as "completed" unless proofs are actually approved
  // If bounty status is "completed" but no proofs are approved, this is a data inconsistency
  const hasApprovedProofs = approvedCount > 0;
  const hasSubmittedProofs = claimRequests.some(c => c.status === 'accepted' && (c.proof_url || c.proof_text) && c.proof_status === 'pending');
  const shouldShowCompleted = b.status === 'completed' && hasApprovedProofs;
  
  // Debug logging to trace status issues
  console.log('[Detail] Bounty Status Debug:', {
    bountyId: b.id,
    bountyStatus: b.status,
    maxClaimers: b.max_claimers,
    acceptedCount,
    approvedCount,
    isFullyClaimed,
    hasApprovedProofs,
    shouldShowCompleted,
    claimedBy: b.claimed_by
  });
  
  // Status transitions belong to the server; partial claim data must never repair a row.
  
  // Role badge configuration
  const roleBadge = isRequester 
    ? { label: 'Bounty Owner', color: 'from-purple-500 to-indigo-600', bg: 'bg-purple-50 dark:bg-purple-950/20', border: 'border-purple-300 dark:border-purple-700' }
    : isHunter 
      ? { label: 'Active Hunter', color: 'from-emerald-500 to-teal-600', bg: 'bg-emerald-50 dark:bg-emerald-950/20', border: 'border-emerald-300 dark:border-emerald-700' }
      : null;

  const handleApproveProof = async (claimId: string) => {
    setBusy(true);
    const { data, error } = await bountyApi.reviewProof(claimId, 'approve');
    setBusy(false);
    if (error || !data?.success) {
      toast.error(error?.message || data?.error || 'Failed to approve proof');
      return;
    }
    const paid = ((data.reward_cents ?? b.reward_cents) / 100).toFixed(2);
    toast.success(`Owner marked satisfied. $${paid} paid to hunter.`);
    // Offer to showcase the accepted proof on the public Bounty Wall (non-blocking)
    const claim = claimRequests.find((c) => c.id === claimId);
    if (claim?.proof_url) {
      setWallDialogClaim({ id: claim.id, proof_url: claim.proof_url });
    }
    load();
    loadClaimRequests();
  };

  const handleConfirmRejectProof = async (reason: string) => {
    if (!rejectDialogClaimId) return;
    setBusy(true);
    const { data, error } = await bountyApi.reviewProof(rejectDialogClaimId, 'reject', reason);
    setBusy(false);
    setRejectDialogClaimId(null);
    if (error || !data?.success) {
      toast.error(error?.message || data?.error || 'Failed to reject proof');
      return;
    }
    toast.success('Proof rejected. Hunter can revise and resubmit.');
    load();
    loadClaimRequests();
  };

  const handleClaimBounty = async () => {
    try {
      console.log('[Claim] 🎯 handleClaimBounty called');
      console.log('[Claim] User:', user?.id);
      console.log('[Claim] Bounty:', b?.id);
      console.log('[Claim] Busy state:', busy);
      console.log('[Claim] Claim message length:', claimMessage.length);
      
      if (!user) { 
        console.log('[Claim] ❌ No user, navigating to auth');
        nav(buildAuthUrl(ROUTES.BOUNTY_DETAIL(b.id), "signin"));
        return; 
      }
      
      // ARMADILLO: Security check before claim submission
      console.log('[Claim] 🔒 Running security check...');
      const securityResult = await validateAction('claim_request', claimMessage || undefined);
      console.log('[Claim] Security result:', securityResult);
      
      if (!securityResult.allowed) {
        console.warn('🦔 [Armadillo] Claim request blocked:', securityResult.reason, securityResult.layerBlocked);
        toast.error(`Security check failed: ${securityResult.reason}`);
        return;
      }
      
      // Check claim permission first
      console.log('[Claim] 🔍 Checking claim permission...');
      const { data: permission, error: permError } = await bountyApi.canClaimBounty(user.id, b.id);
      console.log('[Claim] Permission check result:', { permission, permError });
      
      if (permError || !permission) {
        console.error('[Claim] ❌ Permission check failed:', permError);
        toast.error("Failed to verify claim permission");
        return;
      }
      
      if (!permission.can_claim) {
        console.warn('[Claim] ❌ Claim denied:', permission.reason);
        // Show specific reason
        if (permission.abuse_detected) {
          toast.error("Account under review. Please complete your active claims first.");
        } else if (permission.cooldown_hours) {
          toast.error(`Too many recent rejections. Please wait ${permission.cooldown_hours} hours.`);
        } else if (permission.active_claims !== undefined && permission.claim_limit !== undefined) {
          toast.error(`Claim limit reached: ${permission.active_claims}/${permission.claim_limit} active claims. Complete some first!`);
        } else {
          toast.error(permission.reason || "Cannot claim this bounty");
        }
        return;
      }
      
      // Permission granted, submit claim
      console.log('[Claim] ✅ Permission granted, submitting claim...');
      setBusy(true);
      const { data, error } = await bountyApi.requestClaim(b.id, claimMessage || undefined);
      setBusy(false);
      console.log('[Claim] Submit result:', { data, error });
      
      if (error) { 
        console.error('[Claim] ❌ Submit failed:', error);
        toast.error(error.message || "Failed to submit claim request"); 
        return; 
      }

      // CRITICAL: Check success flag from RPC response — it can return {success: false, error: '...'} without throwing
      if (!data?.success) {
        console.error('[Claim] ❌ RPC returned failure:', data?.error);
        toast.error(data?.error || "Failed to submit claim request");
        return;
      }
      
      if (data?.auto_accepted) {
        console.log('[Claim] ✅ Claim auto-accepted!');
        toast.success("Claim accepted! You can now work on this bounty.");
        setShowClaimForm(false);
        setClaimMessage("");
      } else {
        console.log('[Claim] ✅ Claim request sent, waiting for approval');
        toast.success("Claim request sent! Waiting for bounty poster to approve.", {
          description: "You'll be notified via email when the poster reviews your request."
        });
        setShowClaimForm(false);
        setClaimMessage("");
      }
      
      load();
      loadClaimRequests();
    } catch (error) {
      console.error('[Claim] 💥 Unexpected error:', error);
      setBusy(false);
      toast.error("An unexpected error occurred. Please try again.");
    }
  };

  const handleRespondToClaim = async (claimId: string, action: 'accept' | 'reject') => {
    setBusy(true);
    
    console.log('[Detail] Accepting/Rejecting claim:', {
      claimId,
      action,
      bountyId: b.id,
      currentBountyStatus: b.status,
      maxClaimers: b.max_claimers
    });
    
    const { data, error } = await bountyApi.respondToClaim(claimId, action);
    setBusy(false);
    
    console.log('[Detail] respondToClaim result:', { data, error });
    
    if (error) {
      toast.error(error.message || `Failed to ${action} claim`);
      return;
    }
    
    if (data?.action === 'accept') {
      toast.success("Claim accepted! Hunter has been notified and can now submit proof.", {
        description: "The hunter will receive an email notification with next steps."
      });
    } else {
      toast.success("Claim rejected. Hunter has been notified.");
    }
    
    // Reload to show updated status
    load();
    loadClaimRequests();
  };

  const submitProof = async () => {
    if (!user) return;
    
    // ARMADILLO: Security check before proof submission
    const securityResult = await validateAction('proof_submission', proofText || undefined);
    if (!securityResult.allowed) {
      console.warn('🦔 [Armadillo] Proof submission blocked:', securityResult.reason, securityResult.layerBlocked);
      toast.error(`Security check failed: ${securityResult.reason}`);
      return;
    }
    
    const photoSet = parsePhotoSetRequirement((b as any)?.proof_requirements);
    const effectiveFiles = photoSet
      ? photoSetFiles.filter((f): f is File => f !== null)
      : files;

    if (isFileProofType(proofType) && effectiveFiles.length === 0) {
      toast.error("Please upload your proof file(s)");
      return;
    }
    if (photoSet && effectiveFiles.length < photoSet.min) {
      toast.error(`Please upload all ${photoSet.min} labeled photos`);
      return;
    }
    if ((proofType === "text" || proofType === "link") && !proofText.trim()) {
      toast.error(proofType === "link" ? "Please enter a proof link" : "Please enter your text proof");
      return;
    }
    
    // Run proof requirement validation before submitting
    const requirements = (b as any).proof_requirements || [];
    if (requirements.length > 0) {
      const isValid = await validateProof();
      if (!isValid) {
        toast.error("Proof does not meet all requirements. Please check the errors below.");
        return;
      }
      toast.success("✅ Proof requirements validated!");
    }
    
    setBusy(true);
    let proof_urls: string[] = [];
    
    if (isFileProofType(proofType) && effectiveFiles.length > 0) {
      // Upload all files to storage
      for (const file of effectiveFiles) {
        const { data, error: upErr } = await storageApi.uploadProof(user.id, b.id, file);
        if (upErr) { 
          setBusy(false); 
          toast.error(`Failed to upload ${file.name}: ${upErr.message}`); 
          return; 
        }
        if (data) proof_urls.push(data);
      }
    } else if (proofType === "link") {
      if (!proofText.trim()) {
        setBusy(false);
        toast.error("Please enter a valid link");
        return;
      }
      let linkHref = proofText.trim();
      try {
        linkHref = new URL(linkHref.startsWith("http") ? linkHref : `https://${linkHref}`).href;
      } catch {
        setBusy(false);
        toast.error("Please enter a valid URL");
        return;
      }
      const linkCheck = await validateAction("proof_submission", linkHref);
      if (!linkCheck.allowed) {
        setBusy(false);
        toast.error(linkCheck.reason || "Link blocked by security check");
        return;
      }
      proof_urls = [linkHref];
    }

    if (proofType === "text" && !proofText.trim()) {
      setBusy(false);
      toast.error("Please enter your text proof");
      return;
    }
    
    console.log('[submitProof] Calling RPC with:', {
      bountyId: b.id,
      proofUrl: proof_urls.length > 0 ? proof_urls.join('||') : null,
      proofText: proofType === "text" ? proofText.trim() : null,
      proofType,
      userId: user?.id
    });
    
    const { data: submitData, error } = await bountyApi.submitProof(
      b.id,
      proof_urls.length > 0 ? proof_urls.join('||') : null,
      proofType === "text" ? proofText.trim() : null
    );
    
    console.log('[submitProof] RPC response:', { 
      submitData, 
      error,
      success: submitData?.success,
      message: submitData?.message 
    });
    
    setBusy(false);
    
    // Check for explicit error from RPC
    if (error) { 
      console.error('[submitProof] Proof submission error:', error); 
      toast.error(error.message || 'Failed to submit proof'); 
      return; 
    }
    
    // Check if RPC returned success: false
    if (submitData && submitData.success === false) {
      console.error('[submitProof] RPC returned success: false', submitData.error || submitData.message);
      toast.error(submitData.error || submitData.message || 'Failed to submit proof');
      return;
    }
    
    if (!submitData || submitData.success !== true) {
      console.error('[submitProof] Invalid response from RPC', submitData);
      toast.error('Failed to submit proof - invalid response');
      return;
    }
    
    console.log('[submitProof] ✅ Proof submitted successfully, reloading data...');
    
    // Only the server can confirm a payout. submit_proof handles auto-approve
    // itself and reports the outcome, so never announce payment from the client:
    // approve_bounty is owner-only and would always be refused here.
    if (submitData.auto_approved === true) {
      toast.success(`Proof submitted & auto-approved! $${(b.reward_cents / 100).toFixed(2)} paid out.`);
    } else {
      if (submitData.auto_approve_error) {
        console.warn('[submitProof] Auto-approve did not complete:', submitData.auto_approve_error);
      }
      toast.success(SUCCESS_MESSAGES.PROOF_SUBMITTED);
    }
    
    // PHOENIX: Await reloads to ensure UI updates with latest proof data
    // Use setTimeout to prevent race condition with real-time subscription
    setTimeout(async () => {
      await load();
      await loadClaimRequests();
      console.log('[submitProof] ✅ Data reloaded, proof should now be visible');
    }, 500); // Wait 500ms for database to commit transaction
  };

  return (
    <div className="min-h-screen">
      <Header />
      <main className="container max-w-4xl py-6 space-y-6">
        <Button asChild variant="ghost" size="sm" className="mb-2"><Link to={ROUTES.HOME}><ArrowLeft className="h-4 w-4" /> Back</Link></Button>
          
        {/* Hero Section with Bounty Info */}
        <article className="bg-gradient-to-br from-[hsl(var(--paper))] to-[hsl(var(--muted))] rounded-2xl border-2 border-[hsl(var(--paper-edge))] shadow-lg overflow-hidden">
          {/* Status Banner - PHOENIX: Expired status overrides ALL other states */}
          <div className={`px-6 py-3 ${
            isExpired(b.expires_at) ? 'bg-red-500' :
            hasApprovedProofs ? 'bg-green-600' :
            b.status === "open" ? 'bg-green-500' :
            b.status === "claimed" && hasSubmittedProofs ? 'bg-blue-500' :
            b.status === "claimed" ? 'bg-yellow-500' :
            b.status === "submitted" && !multiClaimerOpen ? 'bg-blue-500' :
            b.status === "submitted" && multiClaimerOpen ? 'bg-green-500' :
            'bg-gray-500'
          } text-white text-center text-sm font-bold uppercase tracking-wider`}>
            {isExpired(b.expires_at) ? 'Expired' :
             hasApprovedProofs ? (
               b.max_claimers && b.max_claimers > 1
                 ? `Claimed ${approvedCount}/${b.max_claimers}`
                 : 'Fully Claimed'
             ) :
             b.status === "open" ? (
               <>
                 Active & Open
                 {b.max_claimers && b.max_claimers > 1 && acceptedCount > 0 && (
                   <span className="ml-2 text-xs opacity-80">
                     ({acceptedCount}/{b.max_claimers} accepted, {approvedCount} approved)
                   </span>
                 )}
               </>
             ) :
             b.status === "claimed" && hasSubmittedProofs ? (
               <>
                 Proof Submitted - Awaiting Review
                 {b.max_claimers && b.max_claimers > 1 && (
                   <span className="ml-2 text-xs opacity-80">
                     ({acceptedCount}/{b.max_claimers} accepted, {approvedCount} approved)
                   </span>
                 )}
               </>
             ) :
             b.status === "claimed" ? (
               <>
                 Claimed - Awaiting Proof
                 {b.max_claimers && b.max_claimers > 1 && (
                   <span className="ml-2 text-xs opacity-80">
                     ({acceptedCount}/{b.max_claimers} accepted, {approvedCount} approved)
                   </span>
                 )}
               </>
             ) :
             b.status === "submitted" && multiClaimerOpen ? (
               <>Active & Open — {slotsRemaining} claim slot{slotsRemaining !== 1 ? "s" : ""} left</>
             ) :
             b.status === "submitted" ? 'Proof Submitted - Awaiting Review' :
             'Cancelled'}
          </div>

          <div className="p-6 space-y-6">
            {/* Role Badge */}
            {roleBadge && (
              <div className={`inline-flex items-center gap-2 px-4 py-2 ${roleBadge.bg} ${roleBadge.border} border rounded-full`}>
                <span className={`inline-flex items-center px-3 py-1 bg-gradient-to-r ${roleBadge.color} text-white text-xs font-bold rounded-full`}>
                  {roleBadge.label}
                </span>
                <span className="text-xs text-muted-foreground">
                  {isRequester ? "You posted this" : "You're working on this"}
                </span>
              </div>
            )}
            
            {/* Title and Reward */}
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
                <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold leading-tight flex-1">{b.title}</h1>
                <div className="flex-shrink-0 text-left sm:text-right">
                  <div className="text-2xl sm:text-3xl lg:text-4xl font-bold text-green-600">${reward}</div>
                  {b.max_claimers > 1 && (
                    <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                      <Users className="h-3 w-3" />
                      {b.max_claimers === 999 ? "Unlimited hunters" : `${b.max_claimers} hunters max`}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Meta Info */}
              <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground pb-4 border-b border-[hsl(var(--paper-edge))]">
                {b.country && (
                  <span className="flex items-center gap-2">
                    <span className="text-base">{countryToFlag(b.country)}</span>
                    {COUNTRY_NAMES[b.country] || b.country}
                  </span>
                )}
                {b.location && (
                  <span className="flex items-center gap-2">
                    <MapPin className="h-4 w-4" />
                    {b.location}
                  </span>
                )}
                <span className="flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  {formatDistance(b.expires_at)}
                </span>
                <span className="flex items-center gap-2">
                  <Shield className="h-4 w-4" />
                  Proof: <strong className="text-foreground capitalize">{b.proof_type}</strong>
                </span>
              </div>
            </div>
            
            {/* Bounty Poster Profile */}
            <div className="flex items-center gap-3 p-4 bg-[hsl(var(--muted))] rounded-lg">
              <User className="h-5 w-5 text-muted-foreground" />
              <Link 
                to={`/profile/${b.requester_id}`}
                className="text-sm font-medium text-primary hover:underline"
              >
                View Bounty Poster's Profile
              </Link>
            </div>
            
            {/* Description */}
            <SafeRichText content={b.description} />
              
            {/* Instructions */}
            {b.instructions && (
              <div className="p-5 bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30 border-2 border-amber-300 dark:border-amber-700 rounded-xl">
                <div className="flex items-center gap-2 mb-3">
                  <FileText className="h-5 w-5 text-amber-600" />
                  <h3 className="font-bold text-sm uppercase tracking-wider text-amber-700 dark:text-amber-300">Instructions</h3>
                </div>
                <p className="text-sm text-foreground whitespace-pre-wrap leading-relaxed">{b.instructions}</p>
              </div>
            )}

            {/* Proof Requirements */}
            {(b as any).proof_requirements?.length > 0 && (
              <div className="p-5 bg-gradient-to-br from-primary/5 to-[hsl(35_75%_42%)]/5 border-2 border-primary/30 rounded-xl">
                <div className="flex items-center gap-2 mb-4">
                  <Shield className="h-5 w-5 text-primary" />
                  <h3 className="font-bold text-sm uppercase tracking-wider text-primary">Proof Requirements</h3>
                  {validationStatus === 'validating' && (
                    <Loader2 className="h-4 w-4 animate-spin text-primary ml-auto" />
                  )}
                  {validationStatus === 'valid' && (
                    <Badge className="ml-auto bg-green-500 text-white text-xs">✓ Validated</Badge>
                  )}
                  {validationStatus === 'invalid' && (
                    <Badge className="ml-auto bg-red-500 text-white text-xs">✗ Failed</Badge>
                  )}
                </div>
                <ol className="space-y-3">
                  {(b as any).proof_requirements.map((req: string, index: number) => {
                    const reqLower = req.toLowerCase();
                    let reqKey: string;
                    if (reqLower.includes('timestamp')) reqKey = 'timestamp';
                    else if (reqLower.includes('gps')) reqKey = 'gps';
                    else if (reqLower.includes('color')) reqKey = 'color_photo';
                    else if (reqLower.includes('before and after') || reqLower.includes('multiple angle')) reqKey = 'multiple_photos';
                    else if (reqLower.includes('video')) reqKey = 'video_proof';
                    else if (reqLower.includes('audio')) reqKey = 'audio_recording';
                    else reqKey = req; // Use full string for custom/info-only requirements
                    // Custom requirements always pass (no automated check available)
                    const isChecked = requirementChecks[reqKey] !== false;
                    const showStatus = validationStatus !== 'idle' && files.length > 0;
                    
                    return (
                      <li key={index} className="flex items-start gap-3">
                        <span className={`flex-shrink-0 w-7 h-7 rounded-full text-sm font-bold flex items-center justify-center ${showStatus ? (isChecked ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600') : 'bg-primary/20 text-primary'}`}>
                          {showStatus ? (isChecked ? <Check className="h-4 w-4" /> : <XCircle className="h-4 w-4" />) : index + 1}
                        </span>
                        <span className={`text-sm leading-relaxed pt-0.5 ${showStatus && !isChecked ? 'text-red-500' : 'text-foreground'}`}>{req}</span>
                      </li>
                    );
                  })}
                </ol>
                {validationErrors.length > 0 && (
                  <div className="mt-4 p-3 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg">
                    <p className="text-sm font-semibold text-red-600 dark:text-red-400 mb-2">Validation Errors:</p>
                    <ul className="text-xs text-red-500 dark:text-red-400 space-y-1">
                      {validationErrors.map((err, i) => (
                        <li key={i}>{err}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        </article>
  
        {/* Reference Photos Gallery */}
        {b && (b as any).reference_photos && (b as any).reference_photos.length > 0 && (
          <div className="bg-gradient-to-br from-[hsl(var(--paper))] to-[hsl(var(--muted))] rounded-2xl border-2 border-[hsl(var(--paper-edge))] p-6 shadow-lg">
            <h3 className="font-bold text-xl mb-4 flex items-center gap-2">
              <ImageIcon className="h-6 w-6 text-primary" />
              Reference Photos ({(b as any).reference_photos.length})
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {signedReferenceUrls.map((url, index) => (
                <button
                  key={index}
                  onClick={() => openLightbox(signedReferenceUrls, index)}
                  className="group relative block aspect-square overflow-hidden rounded-xl border-2 border-[hsl(var(--paper-edge))] hover:border-primary transition-all hover:shadow-xl hover:scale-105 bg-[hsl(var(--muted))] cursor-pointer"
                >
                  <img
                    src={url}
                    alt={`Reference ${index + 1}`}
                    className="absolute inset-0 w-full h-full object-cover"
                    loading="eager"
                    crossOrigin="anonymous"
                    onError={(e) => {
                      const img = e.target as HTMLImageElement;
                      const fallback = photoFallbackUrls[index];
                      if (fallback && img.src !== fallback) {
                        // Funded listings keep assets in funding-draft-assets.
                        img.src = fallback;
                        return;
                      }
                      console.warn(`Failed to load reference photo ${index + 1}:`, url);
                      img.style.display = 'none';
                      // Show fallback placeholder
                      const placeholder = img.parentElement?.querySelector('.photo-placeholder');
                      if (placeholder) {
                        (placeholder as HTMLElement).style.display = 'flex';
                      }
                    }}
                  />
                  {/* Fallback placeholder */}
                  <div className="photo-placeholder absolute inset-0 hidden flex-col items-center justify-center bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]">
                    <ImageIcon className="h-12 w-12 mb-2 opacity-50" />
                    <p className="text-xs font-medium">Photo {index + 1}</p>
                  </div>
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
                  <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                    <p className="text-white text-sm font-medium">
                      Photo {index + 1}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
                
        {/* Action Panel */}
        <div className="poster p-4 pt-8">
          {/* Expired Bounty Notice - PHOENIX: Show for ALL expired bounties regardless of status */}
          {isExpired(b.expires_at) && (
            <div className="p-4 bg-red-50 dark:bg-red-950/20 border-2 border-red-300 dark:border-red-800 rounded-lg text-center">
              <AlertCircle className="h-8 w-8 text-red-600 mx-auto mb-2" />
              <h3 className="font-bold text-lg text-red-700 dark:text-red-300 mb-1">This Bounty Has Expired</h3>
              <p className="text-sm text-red-600 dark:text-red-400">
                This bounty is no longer accepting claims.
              </p>
            </div>
          )}
          
          {/* For Hunters - Claim Form (only if no existing claim) */}
          {/* PHOENIX: Allow claiming if bounty is open OR has available slots (multi-claimer) */}
          {(() => {
            // Simplified canClaim: allow claims unless bounty is done, or user already has a claim
            // Queue system: users can claim on 'claimed'/'submitted' bounties (pending proofs)
            const canClaim = !isExpired(b.expires_at) 
              && !isRequester 
              && !isAcceptedHunter 
              && !isFullyClaimed
              && b.status !== "completed"
              && b.status !== "cancelled"
              && !userRejectedClaim
              && !claimRequests.some(c => c.hunter_id === user?.id);
            
            // Show completed/claimed message for non-participant users when bounty is fully completed
            if (!canClaim && isFullyClaimed && !isRequester && !claimRequests.some(c => c.hunter_id === user?.id)) {
              return (
                <div className="space-y-4 p-6 bg-gradient-to-r from-emerald-50 to-green-50 dark:from-emerald-950/20 dark:to-green-950/20 border-2 border-emerald-300 dark:border-emerald-700 rounded-xl text-center">
                  <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center shadow-lg ring-4 ring-emerald-200 dark:ring-emerald-800">
                    <CheckCircle2 className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="font-display text-xl font-bold text-emerald-900 dark:text-emerald-100 mt-4 mb-2">
                    This bounty has been completed
                  </h3>
                  <p className="text-sm text-emerald-700 dark:text-emerald-300 max-w-md mx-auto">
                    The bounty owner has marked the work as satisfactory and the reward has been paid to the winning hunter.
                  </p>
                </div>
              );
            }
            
            if (!canClaim) return null;
            
            return (
            <div className={`space-y-4 p-4 ${isRequester ? 'bg-purple-50/50 dark:bg-purple-950/10' : isHunter ? 'bg-emerald-50/50 dark:bg-emerald-950/10' : 'bg-blue-50/50 dark:bg-blue-950/10'} border-2 ${isRequester ? 'border-purple-200 dark:border-purple-800' : isHunter ? 'border-emerald-200 dark:border-emerald-800' : 'border-blue-200 dark:border-blue-800'} rounded-lg`}>
              {!user ? (
                <div className="text-center space-y-3">
                  <Shield className="h-12 w-12 text-blue-600 dark:text-blue-400 mx-auto" />
                  <h3 className="font-bold text-lg text-blue-700 dark:text-blue-300">Sign In to Claim This Bounty</h3>
                  <p className="text-sm text-muted-foreground">
                    You need to be signed in to claim bounties and get paid for your work.
                  </p>
                  <Button 
                    className="w-full" 
                    size="lg"
                    onClick={() => nav(buildAuthUrl(ROUTES.BOUNTY_DETAIL(b.id), "signin"))}
                  >
                    Sign In / Sign Up
                  </Button>
                </div>
              ) : !showClaimForm ? (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Users className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                    <h3 className="font-bold text-lg text-blue-700 dark:text-blue-300">Hunter Actions</h3>
                  </div>
                  
                  {/* Check if owner has sufficient balance for payouts */}
                  {b.status === 'claimed' && bountyBalanceStatus && !bountyBalanceStatus.sufficient && (
                    <div className="p-4 bg-amber-50 dark:bg-amber-950/20 border-2 border-amber-400 dark:border-amber-600 rounded-lg mb-4">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertCircle className="h-5 w-5 text-amber-600" />
                        <h4 className="font-bold text-amber-700 dark:text-amber-400">Bounty Temporarily Paused</h4>
                      </div>
                      <p className="text-sm text-amber-600 dark:text-amber-300">
                        The bounty owner needs to top up their balance before more claims can be accepted.
                        {bountyBalanceStatus.reason && `: ${bountyBalanceStatus.reason}`}
                      </p>
                    </div>
                  )}
                  
                  {/* Fully Claimed - No longer accepting claims (only if proofs are actually approved) */}
                  {isFullyClaimed && hasApprovedProofs ? (
                    <div className="p-4 bg-gradient-to-r from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-900 border-2 border-gray-400 dark:border-gray-600 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <CheckCircle2 className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                        <h4 className="font-bold text-gray-700 dark:text-gray-300">Fully Claimed</h4>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        All {b.max_claimers} slots have been filled and approved. This bounty is no longer accepting claims.
                      </p>
                    </div>
                  ) : (
                    <Button 
                      className="w-full" 
                      size="lg"
                      onClick={() => setShowClaimForm(true)}
                      disabled={busy || (bountyBalanceStatus && !bountyBalanceStatus.sufficient)}
                    >
                      Claim this Bounty
                    </Button>
                  )}
                </div>
              ) : (
                <div className="space-y-4 p-4 border-2 border-[hsl(var(--paper-edge))] rounded-lg">
                  <h3 className="font-display text-lg font-bold">Submit Claim Request</h3>
                  <div className="space-y-2">
                    <Label htmlFor="claim-message">Message to Poster (Optional)</Label>
                    <Textarea
                      id="claim-message"
                      placeholder="Introduce yourself and explain why you're perfect for this bounty..."
                      value={claimMessage}
                      onChange={(e) => setClaimMessage(e.target.value)}
                      maxLength={500}
                      rows={4}
                    />
                    <p className="text-xs text-muted-foreground text-right">{claimMessage.length}/500</p>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Button className="w-full sm:flex-1" onClick={handleClaimBounty} disabled={busy}>
                      Submit Claim Request
                    </Button>
                    <Button variant="outline" className="w-full sm:w-auto" onClick={() => setShowClaimForm(false)} disabled={busy}>
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </div>
            );
          })()}
          
          {/* For Hunters with Pending Claim */}
          {userPendingClaim && !isAcceptedHunter && (
            <div className="space-y-4 p-4 bg-gradient-to-r from-amber-50 to-yellow-50 dark:from-amber-950/20 dark:to-yellow-950/20 border-2 border-amber-300 dark:border-amber-700 rounded-lg">
              <div className="flex items-center gap-2 mb-3">
                <Badge className="bg-gradient-to-r from-amber-500 to-yellow-600 text-white">
                  ⏳ Claim Request Pending
                </Badge>
              </div>
              
              <div className="flex items-start gap-3">
                <Clock className="h-6 w-6 text-amber-600 dark:text-amber-400 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-display text-lg font-bold text-amber-900 dark:text-amber-100">Waiting for Owner Review</h3>
                  <p className="text-sm text-amber-800 dark:text-amber-200 mt-1">
                    Your claim request is being reviewed by the bounty owner.
                  </p>
                  <p className="text-xs text-amber-700 dark:text-amber-300 mt-2">
                    💡 You'll be notified when they make a decision
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {/* Claim rejected — no second claim request allowed */}
          {userRejectedClaim && !isAcceptedHunter && !isExpired(b.expires_at) && (
            <div className="space-y-4 p-4 bg-gradient-to-r from-red-50 to-rose-50 dark:from-red-950/20 dark:to-rose-950/20 border-2 border-red-300 dark:border-red-700 rounded-lg opacity-90">
              <div className="flex items-start gap-3">
                <XCircle className="h-6 w-6 text-red-600 dark:text-red-400 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-display text-lg font-bold text-red-900 dark:text-red-100">Claim Request Rejected</h3>
                  <p className="text-sm text-red-800 dark:text-red-200 mt-1">
                    The bounty owner declined your claim request. You cannot submit another claim for this bounty.
                  </p>
                  {userRejectedClaim.rejection_reason && (
                    <p className="text-xs text-red-700 dark:text-red-300 mt-2">
                      Reason: {userRejectedClaim.rejection_reason}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Accepted hunter — prompt to send proof before submission */}
          {userAcceptedClaim && !isRequester && !isExpired(b.expires_at) &&
            userAcceptedClaim.status === 'accepted' &&
            !userAcceptedClaim.proof_url && !userAcceptedClaim.proof_text &&
            userAcceptedClaim.proof_status !== 'approved' && (
            <div className="p-4 bg-emerald-50 dark:bg-emerald-950/20 border-2 border-emerald-400 dark:border-emerald-600 rounded-lg mb-4">
              <h3 className="font-display text-lg font-bold text-emerald-900 dark:text-emerald-100">
                Your claim request has been accepted — please send proof
              </h3>
              <p className="text-sm text-emerald-800 dark:text-emerald-200 mt-1">
                Complete the work below and submit proof for the owner to review.
              </p>
            </div>
          )}
  
          {/* For Bounty Poster - Extension & Claim Management */}
          {isRequester && (
            <div className="space-y-4 p-4 bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-950/20 dark:to-indigo-950/20 border-2 border-purple-300 dark:border-purple-700 rounded-lg">
              <div className="flex items-center gap-2 mb-3">
                <Badge className="bg-gradient-to-r from-purple-500 to-indigo-600 text-white">
                  Owner Controls
                </Badge>
                <span className="text-xs text-muted-foreground">Manage your bounty</span>
              </div>
              
              {/* Claims Counter */}
              {b.status === "open" && !isExpired(b.expires_at) && (
                <div className="p-3 bg-white/50 dark:bg-black/20 border border-purple-200 dark:border-purple-800 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Users className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                      <span className="font-bold text-purple-900 dark:text-purple-100">
                        {claimRequests.filter(c => c.status === 'accepted').length} / {b.max_claimers || 1} Claims Filled
                      </span>
                    </div>
                    <span className="text-sm text-purple-700 dark:text-purple-300">
                      {(b.max_claimers || 1) - claimRequests.filter(c => c.status === 'accepted').length} Remaining
                    </span>
                  </div>
                  <div className="mt-2 bg-purple-200 dark:bg-purple-900 rounded-full h-2">
                    <div 
                      className="bg-gradient-to-r from-purple-500 to-indigo-600 h-2 rounded-full transition-all"
                      style={{ width: `${(claimRequests.filter(c => c.status === 'accepted').length / (b.max_claimers || 1)) * 100}%` }}
                    />
                  </div>
                </div>
              )}
              
              {/* Deletion Countdown for Expired/Cancelled/Completed Bounties - PHOENIX: Check expiration for ALL statuses */}
              {isExpired(b.expires_at) || b.status === "cancelled" || b.status === "completed" ? (
                <div className="p-4 bg-amber-50 dark:bg-amber-950/20 border-2 border-amber-300 dark:border-amber-700 rounded-lg mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Timer className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                    <h4 className="font-bold text-sm text-amber-700 dark:text-amber-300">Auto-Deletion Countdown</h4>
                  </div>
                  <p className="text-2xl font-bold text-amber-600 dark:text-amber-400 mb-1">
                    {timeUntilDeletion}
                  </p>
                  <p className="text-xs text-amber-600 dark:text-amber-400">
                    This bounty will be automatically deleted in {daysUntilDeletion} days
                  </p>
                </div>
              ) : null}
              
              {/* Edit & Delete Buttons */}
              <div className="grid grid-cols-2 gap-2 mb-4">
                <Button 
                  className="w-full"
                  variant="outline"
                  size="lg"
                  onClick={handleEditBounty}
                  disabled={isExpired(b.expires_at)}
                >
                  <Edit className="h-4 w-4 mr-2" />
                  {isExpired(b.expires_at) ? "Expired" : "Edit Bounty"}
                </Button>
                <Button 
                  className="w-full"
                  variant="destructive"
                  size="lg"
                  onClick={() => setShowDeleteDialog(true)}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </Button>
              </div>
              
              {/* Extension Button */}
              {b.status === 'open' && !isExpired(b.expires_at) && (
                <Button 
                  className="w-full" 
                  variant="outline"
                  size="lg"
                  onClick={() => setShowExtensionDialog(true)}
                >
                  <Clock className="h-4 w-4 mr-2" />
                  Extend Deadline
                </Button>
              )}
              
              {/* Claim Requests */}
              {claimRequests.length > 0 && (
            <div className="mt-6 pt-6 border-t border-[hsl(var(--paper-edge))]">
              <h3 className="font-display text-lg font-bold flex items-center gap-2 mb-4">
                <Users className="h-5 w-5 text-primary" />
                Claim Requests
                <Badge variant="secondary" className="ml-auto">{claimRequests.length}</Badge>
              </h3>
              {loadingClaims ? (
                <Skeleton className="h-24" />
              ) : (
                <div className="space-y-3">
                  {claimRequests.map((claim) => {
                    const rep = hunterReputations[claim.hunter_id];
                    return (
                    <div key={claim.id} className="group relative bg-gradient-to-br from-[hsl(var(--paper))] to-[hsl(var(--muted))] rounded-xl border-2 border-[hsl(var(--paper-edge))] hover:border-primary/50 transition-all hover:shadow-lg overflow-hidden">
                      {/* Status indicator line */}
                      <div className={`absolute top-0 left-0 right-0 h-1 ${
                        claim.status === 'accepted' ? 'bg-gradient-to-r from-emerald-500 to-green-500' :
                        claim.status === 'rejected' ? 'bg-gradient-to-r from-red-500 to-rose-500' :
                        'bg-gradient-to-r from-amber-500 to-yellow-500'
                      }`} />
                      
                      <div className="p-5 space-y-4">
                        {/* Header: Name, PRO badge, status */}
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 space-y-2">
                            <div className="flex items-center gap-2 flex-wrap">
                              <div className="flex items-center gap-2">
                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                                  <User className="h-5 w-5 text-primary" />
                                </div>
                                <div>
                                  <p className="font-bold text-base">{claim.hunter_name || "Anonymous Hunter"}</p>
                                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                    <Calendar className="h-3 w-3" />
                                    {new Date(claim.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                  </div>
                                </div>
                              </div>
                              {rep?.is_pro && (
                                <Badge className="bg-gradient-to-r from-[hsl(45_85%_55%)] to-[hsl(35_75%_42%)] text-[hsl(25_50%_12%)] font-semibold">
                                  PRO
                                </Badge>
                              )}
                            </div>
                          </div>
                          
                          {/* Status Badge - PHOENIX: Distinguish between claim accepted vs proof approved */}
                          <Badge
                            variant={
                              claim.proof_status === 'approved' ? 'default' :
                              claim.proof_status === 'resubmission_requested' ? 'destructive' :
                              claim.status === "accepted" && !claim.proof_url && !claim.proof_text ? "secondary" :
                              claim.status === "accepted" ? "default" :
                              claim.status === "rejected" ? "destructive" :
                              "secondary"
                            }
                            className="flex items-center gap-1 px-3 py-1.5"
                          >
                            {claim.proof_status === 'approved' && <CheckCircle2 className="h-3.5 w-3.5" />}
                            {claim.proof_status !== 'approved' && claim.proof_status !== 'resubmission_requested' && claim.status === "pending" && <AlertCircle className="h-3.5 w-3.5" />}
                            {claim.status === "accepted" && !claim.proof_url && !claim.proof_text && <Clock className="h-3.5 w-3.5" />}
                            {claim.status === "accepted" && (claim.proof_url || claim.proof_text) && <CheckCircle2 className="h-3.5 w-3.5" />}
                            {claim.status === "rejected" && <XCircle className="h-3.5 w-3.5" />}
                            <span className="capitalize">
                              {claim.proof_status === 'approved' ? 'Approved' : 
                               claim.proof_status === 'resubmission_requested' ? 'Rejected' :
                               claim.status === "accepted" && !claim.proof_url && !claim.proof_text ? 'Accepted - Awaiting Proof' :
                               claim.status}
                            </span>
                          </Badge>
                          
                          {/* Show "Reward Paid" badge ONLY for approved proofs (not just accepted claims) */}
                          {claim.proof_status === 'approved' && (
                            <Badge className="bg-emerald-600 text-white">
                              <Award className="h-3.5 w-3.5 mr-1" /> Reward Paid
                            </Badge>
                          )}
                        </div>

                        {/* Bounty Wall: published badge or publish action for approved proofs */}
                        {isRequester && claim.proof_status === 'approved' && (
                          <div>
                            {wallEntriesByClaim[claim.id] ? (
                              <a
                                href={`/wall/${wallEntriesByClaim[claim.id].slug}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gradient-to-r from-[hsl(35_75%_42%)] to-[hsl(25_60%_30%)] text-white text-sm font-semibold hover:opacity-90 transition-opacity"
                              >
                                <Trophy className="h-4 w-4" /> Published on the Bounty Wall
                              </a>
                            ) : (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setWallDialogClaim({ id: claim.id, proof_url: claim.proof_url })}
                                disabled={busy}
                              >
                                <Trophy className="h-4 w-4 mr-2" /> Publish to Wall
                              </Button>
                            )}
                          </div>
                        )}
                        
                        {isRequester && claim.status === "accepted" && !claim.proof_url && !claim.proof_text && claim.proof_status !== 'approved' && (
                          <div className="p-3 bg-amber-50 dark:bg-amber-950/20 border border-amber-300 dark:border-amber-700 rounded-lg">
                            <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                              Awaiting proof from this hunter
                            </p>
                          </div>
                        )}

                        {isRequester && claim.status === "accepted" && claim.proof_status === 'resubmission_requested' && (
                          <div className="p-3 bg-orange-50 dark:bg-orange-950/20 border border-orange-300 dark:border-orange-700 rounded-lg">
                            <p className="text-sm font-medium text-orange-800 dark:text-orange-200">
                              Awaiting revised proof (revision {claim.resubmission_count || 0} / {maxResubmissions})
                            </p>
                          </div>
                        )}

                        {/* Proof Review Section for Owner — pending proof only */}
                        {isRequester && claim.status === "accepted" && 
                         (claim.proof_url || claim.proof_text) &&
                         claim.proof_status === 'pending' && (
                          <div className="mt-6">
                            {/* Header with gradient background */}
                            <div className="relative mb-5">
                              <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/10 via-teal-500/10 to-cyan-500/10 rounded-2xl blur-xl" />
                              <div className="relative p-5 bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-950/30 dark:to-teal-950/30 backdrop-blur-sm border border-emerald-200 dark:border-emerald-800 rounded-2xl shadow-lg">
                                <div className="flex items-center gap-4">
                                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg ring-4 ring-emerald-200 dark:ring-emerald-800">
                                    <CheckCircle2 className="h-6 w-6 text-white" />
                                  </div>
                                  <div className="flex-1">
                                    <h4 className="font-bold text-lg text-gray-900 dark:text-white mb-1">Review Submitted Proof</h4>
                                    <p className="text-sm text-muted-foreground">{claim.hunter_name || "Hunter"} has completed the work</p>
                                  </div>
                                  <Badge className="bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300 border-emerald-300 dark:border-emerald-700">
                                    {(claimProofUrls[claim.id]?.length ?? 0)} file{(claimProofUrls[claim.id]?.length ?? 0) !== 1 ? 's' : ''}
                                  </Badge>
                                </div>
                              </div>
                            </div>
                            
                            {/* Display Submitted Proofs - All File Types */}
                            <div className="mb-4">
                              <Label className="text-emerald-900 dark:text-emerald-100 mb-2 block">
                                Submitted Proof Files ({(claimProofUrls[claim.id]?.length ?? 0)}):
                              </Label>
                              
                              {(isImageProofType(proofType)) && (
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                  {(claimProofUrls[claim.id] || []).map((url, index) => (
                                    <div 
                                      key={index} 
                                      className="group relative aspect-square rounded-xl overflow-hidden border-2 border-gray-200 dark:border-gray-700 cursor-pointer hover:border-emerald-400 dark:hover:border-emerald-500 transition-all duration-300 hover:shadow-xl hover:shadow-emerald-500/20 hover:-translate-y-1 bg-white dark:bg-gray-900"
                                      onClick={async () => {
                                        // Regenerate signed URL to ensure it's fresh
                                        let freshUrl = url;
                                        if (url.startsWith('http') && url.includes('supabase.co')) {
                                          const { data, error } = await storageApi.getSignedProofUrl(url);
                                          if (!error && data) {
                                            freshUrl = data;
                                          }
                                        }
                                        setProofViewerUrl(freshUrl);
                                        setProofViewerType('image');
                                      }}
                                    >
                                      <img 
                                        src={url} 
                                        alt={`Proof ${index + 1}`}
                                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" 
                                        loading="lazy"
                                        crossOrigin="anonymous"
                                        onError={(e) => {
                                          console.warn(`[Detail] Owner review proof ${index + 1} failed to load:`, url);
                                          const img = e.currentTarget;
                                          img.style.opacity = '0.3';
                                          const parent = img.parentElement;
                                          if (parent) {
                                            const errorLabel = document.createElement('div');
                                            errorLabel.className = 'absolute inset-0 flex items-center justify-center text-xs text-gray-500 font-medium pointer-events-none';
                                            errorLabel.textContent = 'Failed to load';
                                            parent.appendChild(errorLabel);
                                          }
                                        }}
                                      />
                                      {/* Gradient overlay on hover */}
                                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-between p-4">
                                        <div className="text-white">
                                          <p className="text-sm font-bold mb-1">Proof {index + 1}</p>
                                          <p className="text-xs opacity-90 flex items-center gap-1">
                                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                                            </svg>
                                            Click to expand
                                          </p>
                                        </div>
                                        <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center border border-white/30">
                                          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                                          </svg>
                                        </div>
                                      </div>
                                      {/* File number badge */}
                                      <div className="absolute top-3 left-3 w-8 h-8 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 text-white text-xs font-bold flex items-center justify-center shadow-lg border-2 border-white/30">
                                        {index + 1}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                              
                              {proofType === "video" && (claimProofUrls[claim.id] || []).map((url, index) => (
                                <div key={index} className="rounded-lg overflow-hidden border-2 border-emerald-200 dark:border-emerald-700">
                                  <video src={url} controls className="w-full max-h-96" />
                                </div>
                              ))}

                              {(proofType === "pdf" || proofType === "document") && (claimProofUrls[claim.id] || []).map((url, index) => (
                                <div key={index} className="flex flex-col sm:flex-row gap-2">
                                  <Button
                                    type="button"
                                    variant="outline"
                                    className="flex-1 justify-start"
                                    onClick={() => openProofDocument(url)}
                                  >
                                    <FileText className="h-4 w-4 mr-2" />
                                    Open PDF {index + 1}
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="secondary"
                                    className="flex-1 justify-start"
                                    asChild
                                  >
                                    <a href={url} download target="_blank" rel="noopener noreferrer">
                                      Download PDF {index + 1}
                                    </a>
                                  </Button>
                                </div>
                              ))}
                              
                              {/* Audio Files */}
                              {proofType === "audio" && (claimProofUrls[claim.id]?.length ?? 0) > 0 && (
                                <div className="space-y-3">
                                  <div className="flex items-center gap-2 mb-3">
                                    <Mic className="h-5 w-5 text-purple-600" />
                                    <h4 className="font-bold text-purple-900 dark:text-purple-100">Audio Proof Recording{signedProofUrls.length > 1 ? 's' : ''}</h4>
                                  </div>
                                  
                                  {(claimProofUrls[claim.id] || []).map((url, index) => (
                                    <div key={index} className="p-4 bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-950/20 dark:to-indigo-950/20 border-2 border-purple-300 dark:border-purple-700 rounded-lg">
                                      <div className="flex items-center gap-2 mb-2">
                                        <Badge className="bg-purple-600 text-white text-xs">
                                          Audio {index + 1}
                                        </Badge>
                                      </div>
                                      <AudioPlayer
                                        audioUrl={url}
                                        autoPlay={false}
                                      />
                                    </div>
                                  ))}
                                </div>
                              )}
                              
                              {(proofType === "document") && (claimProofUrls[claim.id] || []).map((url, index) => (
                                <div key={index} className="space-y-2">
                                  <a
                                    href={url}
                                    download
                                    className="flex items-center gap-3 p-3 bg-white dark:bg-gray-800 rounded-lg border-2 border-emerald-200 dark:border-emerald-700 hover:border-emerald-400 dark:hover:border-emerald-500 transition-colors cursor-pointer"
                                  >
                                    <FileText className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                                    <div className="flex-1">
                                      <p className="text-sm font-medium text-emerald-900 dark:text-emerald-100">Download Proof {index + 1}</p>
                                      <p className="text-xs text-muted-foreground">Click to download</p>
                                    </div>
                                  </a>
                                </div>
                              ))}
                              
                              {/* Text Proof - Display the actual text content */}
                              {proofType === "text" && claim.proof_text && (
                                <div className="p-4 bg-white dark:bg-gray-800 rounded-lg border-2 border-emerald-200 dark:border-emerald-700">
                                  <div className="flex items-start gap-3 mb-3">
                                    <FileText className="h-5 w-5 text-emerald-600 dark:text-emerald-400 mt-0.5" />
                                    <div className="flex-1">
                                      <p className="text-sm font-semibold text-emerald-900 dark:text-emerald-100 mb-1">Submitted Text Proof:</p>
                                      <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{claim.proof_text}</p>
                                    </div>
                                  </div>
                                </div>
                              )}
                              
                              {/* Link Proof - Display the URL */}
                              {proofType === "link" && claim.proof_url && (
                                <SafeProofLink url={claim.proof_url.split("||")[0]} />
                              )}
                            </div>
                            
                            <div className="flex flex-col sm:flex-row gap-2">
                              <Button 
                                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                                onClick={() => handleApproveProof(claim.id)}
                                disabled={busy}
                              >
                                <CheckCircle2 className="h-4 w-4 mr-2" /> Mark Satisfied
                              </Button>
                              <Button 
                                variant="outline"
                                className="flex-1 border-red-500 text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30"
                                onClick={() => setRejectDialogClaimId(claim.id)}
                                disabled={busy || (claim.resubmission_count || 0) >= maxResubmissions}
                              >
                                <XCircle className="h-4 w-4 mr-2" /> Not Satisfied
                              </Button>
                            </div>
                          </div>
                        )}
                        
                        {/* Stats Grid */}
                        {rep && (
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-3 bg-[hsl(var(--background))] rounded-lg border border-[hsl(var(--paper-edge))]">
                            <div className="flex flex-col items-center text-center">
                              <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
                                <Award className="h-3.5 w-3.5 text-emerald-600" />
                                <span>Completed</span>
                              </div>
                              <p className="text-lg font-bold text-emerald-600">{rep.total_completed}</p>
                            </div>
                            <div className="flex flex-col items-center text-center">
                              <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
                                <TrendingUp className="h-3.5 w-3.5 text-blue-600" />
                                <span>Success Rate</span>
                              </div>
                              <p className="text-lg font-bold text-blue-600">
                                {rep.completion_rate !== null ? `${rep.completion_rate}%` : 'N/A'}
                              </p>
                            </div>
                            <div className="flex flex-col items-center text-center">
                              <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
                                <Target className="h-3.5 w-3.5 text-purple-600" />
                                <span>Active</span>
                              </div>
                              <p className="text-lg font-bold text-purple-600">{rep.active_claims}</p>
                            </div>
                            <div className="flex flex-col items-center text-center">
                              <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
                                <XCircle className={`h-3.5 w-3.5 ${rep.recent_rejections > 0 ? 'text-red-600' : 'text-muted-foreground'}`} />
                                <span>Rejected</span>
                              </div>
                              <p className={`text-lg font-bold ${rep.recent_rejections > 0 ? 'text-red-600' : 'text-muted-foreground'}`}>
                                {rep.recent_rejections || 0}
                              </p>
                            </div>
                          </div>
                        )}
                        
                        {/* Smart Warning for Owners */}
                        {rep && rep.active_claims >= 10 && rep.completion_rate === 0 && (
                          <div className="p-3 bg-red-50 dark:bg-red-950/30 border-l-4 border-red-500 rounded-r-lg flex items-start gap-2">
                            <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                            <p className="text-xs text-red-700 dark:text-red-400 font-medium">
                              Warning: High activity with 0% completion - possible claim hoarder
                            </p>
                          </div>
                        )}
                        
                        {/* Message */}
                        {claim.message && (
                          <div className="relative">
                            <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-primary/50 to-primary/30 rounded-full" />
                            <div className="pl-4 py-2">
                              <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-2">
                                <MessageCircle className="h-3.5 w-3.5" />
                                <span>Hunter's Message</span>
                              </div>
                              <p className="text-sm leading-relaxed bg-[hsl(var(--background))] p-3 rounded-lg border border-[hsl(var(--paper-edge))]">
                                {claim.message}
                              </p>
                            </div>
                          </div>
                        )}
                        
                        {/* Action Buttons */}
                        {claim.status === "pending" && (
                          <div className="flex gap-2 pt-2">
                            <Button
                              size="sm"
                              onClick={() => handleRespondToClaim(claim.id, 'accept')}
                              disabled={busy}
                              className="flex-1 bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 shadow-sm"
                            >
                              <CheckCircle2 className="h-4 w-4 mr-2" /> Accept
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleRespondToClaim(claim.id, 'reject')}
                              disabled={busy}
                              className="flex-1 border-red-500 text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30"
                            >
                              <XCircle className="h-4 w-4 mr-2" /> Reject
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          
              {/* No claims yet message */}
              {b.status === "open" && !isExpired(b.expires_at) && claimRequests.length === 0 && (
                <p className="text-sm text-muted-foreground text-center">Waiting for hunters to submit claim requests…</p>
              )}
            </div>
          )}
  
          {/* For Accepted Hunters - Submit Proof OR Awaiting Verification (NOT for bounty owner) */}
          {/* PHOENIX EXPIRATION RULE: Expired status overrides all other states */}
          {isAcceptedHunter && !isRequester && !isExpired(b.expires_at) && (b.status === "claimed" || b.status === "submitted" || b.status === "completed" || b.status === "cancelled" || b.status === "open") && (
            <div className="space-y-4 p-4 bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-950/20 dark:to-teal-950/20 border-2 border-emerald-300 dark:border-emerald-700 rounded-lg">
              <div className="flex items-center gap-2 mb-3">
                <Badge className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white">
                  Hunter Actions
                </Badge>
                <span className="text-xs text-muted-foreground">Submit your work</span>
              </div>
              
              {/* Check if hunter has already submitted proof - Phoenix: check claim_requests */}
              {/* Check if hunter has already submitted proof - Phoenix: check claim_requests first, then bounties for backwards compat */}
              {(() => {
                const userClaim = claimRequests.find(c => c.hunter_id === user?.id);
                const hasProof = userClaim && (userClaim.proof_url || userClaim.proof_text);
                const bountyHasProof = b.proof_url || b.proof_text;
                
                console.log('[Detail] Proof Submission Check:', {
                  userClaim,
                  hasProof,
                  bountyHasProof,
                  shouldShowProofSubmitted: !!(hasProof || bountyHasProof)
                });
                
                return !!(hasProof || bountyHasProof);
              })() ? (() => {
                const userClaim = claimRequests.find(c => c.hunter_id === user?.id);
                const proofApproved = userClaim?.proof_status === 'approved';
                const bountyCompleted = b.status === 'completed';
                
                return (
                // PROOF SUBMITTED - Show status-based message
                <div className="space-y-3">
                  {proofApproved || bountyCompleted ? (
                    // PROOF ACCEPTED
                    <div className="flex items-start gap-3 p-3 bg-green-100 dark:bg-green-900/30 rounded-lg border-2 border-green-400 dark:border-green-600">
                      <CheckCircle2 className="h-6 w-6 text-green-600 dark:text-green-400 mt-1 flex-shrink-0" />
                      <div>
                        <h3 className="font-display text-lg font-bold text-green-900 dark:text-green-100">Owner marked satisfied — you were paid</h3>
                        <p className="text-sm text-green-800 dark:text-green-200 mt-1">
                          The bounty owner approved your proof. Your reward has been added to your wallet.
                        </p>
                        <p className="text-xs text-green-700 dark:text-green-300 mt-2">
                          💰 Payment has been added to your wallet balance
                        </p>
                      </div>
                    </div>
                  ) : userClaim?.proof_status === 'resubmission_requested' ? (
                    // PROOF REJECTED - Show resubmission UI
                    <div className="space-y-4">
                      <div className="flex items-start gap-3 p-4 bg-gradient-to-r from-orange-50 to-red-50 dark:from-orange-950/20 dark:to-red-950/20 border-2 border-orange-300 dark:border-orange-700 rounded-lg">
                        <XCircle className="h-6 w-6 text-orange-600 dark:text-orange-400 mt-1 flex-shrink-0" />
                        <div className="flex-1">
                          <h3 className="font-display text-lg font-bold text-orange-900 dark:text-orange-100">Your proof requires modification</h3>
                          <p className="text-sm text-orange-800 dark:text-orange-200 mt-1">
                            Bounty owner not satisfied. Please address their feedback and resubmit.
                          </p>
                          
                          {/* Show rejection reason if available */}
                          {userClaim.rejection_reason && (
                            <div className="mt-3 p-3 bg-white dark:bg-gray-900 rounded-lg border border-orange-200 dark:border-orange-800">
                              <p className="text-xs font-semibold text-orange-700 dark:text-orange-300 mb-1">Owner's Feedback:</p>
                              <p className="text-sm text-gray-900 dark:text-gray-100">{userClaim.rejection_reason}</p>
                            </div>
                          )}
                          
                          {/* Show resubmission counter */}
                          <div className="mt-3 flex items-center gap-2 text-xs text-orange-700 dark:text-orange-300">
                            <span>Resubmission attempt:</span>
                            <span className="font-bold">{userClaim.resubmission_count || 0} / {maxResubmissions}</span>
                            {maxResubmissions - (userClaim.resubmission_count || 0) > 0 ? (
                              <span className="text-green-600 dark:text-green-400 ml-2">
                                ✓ {maxResubmissions - (userClaim.resubmission_count || 0)} revisions remaining
                              </span>
                            ) : (
                              <span className="text-red-600 dark:text-red-400 ml-2">
                                ✗ No attempts remaining
                              </span>
                            )}
                          </div>
                          
                          <p className="text-xs text-orange-700 dark:text-orange-300 mt-3">
                            💡 Submit an improved proof that addresses the owner's feedback
                          </p>
                        </div>
                      </div>
                      
                      {(userClaim.resubmission_count || 0) >= maxResubmissions ? (
                        <div className="p-4 bg-gray-100 dark:bg-gray-900/50 border-2 border-gray-300 dark:border-gray-700 rounded-lg opacity-60 pointer-events-none">
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            Maximum proof revisions reached. You can no longer submit proof for this bounty.
                          </p>
                        </div>
                      ) : (
                      <div className="p-4 bg-white dark:bg-gray-900 rounded-lg border-2 border-orange-200 dark:border-orange-800">
                        <h4 className="font-bold text-sm mb-3">Submit Revised Proof</h4>
                        
                        {isImageProofType(proofType) && (
                          <div className="space-y-3">
                            <div className="space-y-1">
                              <Label htmlFor="file-resubmit">
                                <Upload className="h-4 w-4 inline mr-1" />
                                Upload New {b.proof_type === "photo" ? "Photo" : "Screenshot"}
                              </Label>
                              <Input 
                                id="file-resubmit" 
                                type="file" 
                                accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
                                multiple
                                onChange={handleFileSelect}
                              />
                              <p className="text-xs text-muted-foreground">
                                Max size: 50MB per file
                              </p>
                            </div>
                            
                            {/* File Previews */}
                            {filePreviews.length > 0 && (
                              <div className="space-y-2 mt-4">
                                <Label>Selected Files ({files.length})</Label>
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                  {filePreviews.map((preview, index) => (
                                    <div key={index} className="relative group">
                                      {preview.startsWith('http') || preview.startsWith('blob:') ? (
                                        <div className="aspect-square rounded-lg overflow-hidden border-2 border-orange-200 dark:border-orange-800">
                                          <img 
                                            src={preview} 
                                            alt={`Preview ${index + 1}`}
                                            className="w-full h-full object-cover"
                                          />
                                        </div>
                                      ) : (
                                        <div className="aspect-square rounded-lg overflow-hidden border-2 border-orange-200 dark:border-orange-800 bg-orange-50 dark:bg-orange-950/20 flex items-center justify-center">
                                          <FileText className="h-8 w-8 text-orange-600" />
                                        </div>
                                      )}
                                      <button
                                        onClick={() => removeFile(index)}
                                        className="absolute top-1 right-1 w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center shadow-lg"
                                      >
                                        <X className="h-3 w-3" />
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                        {proofType === "video" && (
                          <div className="space-y-1">
                            <Label htmlFor="file-resubmit-video">Upload revised video(s)</Label>
                            <Input id="file-resubmit-video" type="file" accept="video/*" multiple onChange={handleFileSelect} />
                            <p className="text-xs text-muted-foreground">Max {MAX_VIDEO_FILES} videos, {MAX_VIDEO_DURATION_SECONDS}s each</p>
                          </div>
                        )}
                        {(proofType === "document" || proofType === "pdf") && (
                          <div className="space-y-1">
                            <Label htmlFor="file-resubmit-doc">Upload revised document(s)</Label>
                            <Input id="file-resubmit-doc" type="file" accept="application/pdf,.doc,.docx" multiple onChange={handleFileSelect} />
                          </div>
                        )}
                        {proofType === "audio" && (
                          <AudioRecorder
                            onRecordingComplete={(file) => {
                              setFiles([file]);
                              setFilePreviews([URL.createObjectURL(file)]);
                            }}
                            maxDurationSeconds={120}
                            maxSizeMB={10}
                          />
                        )}
                        {proofType === "text" && (
                          <div className="space-y-1">
                            <Label htmlFor="txt-resubmit">Your revised answer</Label>
                            <Textarea 
                              id="txt-resubmit" 
                              rows={4} 
                              value={proofText} 
                              onChange={(e) => setProofText(e.target.value)} 
                              maxLength={1000}
                              placeholder="Provide an improved answer..."
                            />
                          </div>
                        )}
                        {proofType === "link" && (
                          <div className="space-y-1">
                            <Label htmlFor="link-resubmit">Your revised link</Label>
                            <Input 
                              id="link-resubmit" 
                              type="url" 
                              value={proofText} 
                              onChange={(e) => setProofText(e.target.value)}
                              placeholder="https://..."
                            />
                          </div>
                        )}
                        
                        <Button 
                          className="w-full mt-4 bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700" 
                          onClick={submitProof} 
                          disabled={!canSubmitProofForm}
                        >
                          {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                          {busy ? 'Submitting...' : `Submit Revised Proof (${maxResubmissions - (userClaim.resubmission_count || 0)} left)`}
                        </Button>
                        {proofType === "video" && !proofFilesValid && (
                          <p className="text-xs text-red-600 mt-2">Fix video length (max 60s) before submitting.</p>
                        )}
                      </div>
                      )}
                    </div>
                  ) : b.status === 'cancelled' ? (
                    // PROOF REJECTED (NO RESUBMISSION ALLOWED)
                    <div className="flex items-start gap-3 p-3 bg-red-100 dark:bg-red-900/30 rounded-lg border-2 border-red-400 dark:border-red-600">
                      <XCircle className="h-6 w-6 text-red-600 dark:text-red-400 mt-1 flex-shrink-0" />
                      <div>
                        <h3 className="font-display text-lg font-bold text-red-900 dark:text-red-100">Proof Not Accepted</h3>
                        <p className="text-sm text-red-800 dark:text-red-200 mt-1">
                          The bounty owner did not accept your proof submission.
                        </p>
                        <p className="text-xs text-red-700 dark:text-red-300 mt-2">
                          💡 Contact the bounty owner for more details
                        </p>
                      </div>
                    </div>
                  ) : (
                    // AWAITING VERIFICATION (claimed or open status)
                    <div className="flex items-start gap-3 p-3 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg border-2 border-emerald-400 dark:border-emerald-600">
                      <Clock className="h-6 w-6 text-emerald-600 dark:text-emerald-400 mt-1 flex-shrink-0" />
                      <div>
                        <h3 className="font-display text-lg font-bold text-emerald-900 dark:text-emerald-100">Proof sent — awaiting verification</h3>
                        <p className="text-sm text-emerald-800 dark:text-emerald-200 mt-1">
                          Your proof has been sent to the bounty owner for review.
                        </p>
                        <p className="text-xs text-emerald-700 dark:text-emerald-300 mt-2">
                          💡 You'll be notified when the owner makes a decision
                        </p>
                      </div>
                    </div>
                  )}
                  
                  {/* Show submitted proof preview if photo/screenshot */}
                  {signedProofUrls.length > 0 && (b.proof_type === "photo" || b.proof_type === "screenshot") && (
                    <div className="mt-3">
                      <Label>Your Submitted Proof{signedProofUrls.length > 1 ? 's' : ''} ({signedProofUrls.length}):</Label>
                      <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {signedProofUrls.map((url, index) => (
                          <div key={index} className="rounded-lg overflow-hidden border-2 border-emerald-300 dark:border-emerald-700 relative group">
                            <img 
                              src={url} 
                              alt={`Your submitted proof ${index + 1}`}
                              className="w-full h-auto max-h-80 object-contain bg-black/5 dark:bg-black/20 cursor-pointer transition-transform hover:scale-105"
                              onClick={() => {
                                setProofViewerUrl(url);
                                setProofViewerType('image');
                              }}
                              onError={async (e) => {
                                console.warn(`[Detail] Proof image ${index + 1} failed to load:`, url);
                                const img = e.currentTarget;
                                // Try to regenerate the signed URL
                                if (url && url.includes('supabase.co')) {
                                  console.log('[Detail] Attempting to regenerate signed URL...');
                                  const { data, error } = await storageApi.getSignedProofUrl(url);
                                  if (!error && data && data !== url) {
                                    console.log('[Detail] Regenerated URL, retrying load');
                                    img.src = data;
                                    return;
                                  }
                                }
                                // If regeneration fails, show error state
                                img.style.display = 'none';
                                const parent = img.parentElement;
                                if (parent) {
                                  const errorDiv = document.createElement('div');
                                  errorDiv.className = 'flex flex-col items-center justify-center py-12 text-muted-foreground';
                                  errorDiv.innerHTML = `
                                    <svg class="h-12 w-12 mb-2 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                    <p class="text-sm font-medium">Unable to load proof ${index + 1}</p>
                                    <p class="text-xs mt-1">Click to retry</p>
                                  `;
                                  errorDiv.onclick = () => window.location.reload();
                                  parent.appendChild(errorDiv);
                                }
                              }}
                            />
                            {/* Proof number badge */}
                            <div className="absolute top-2 left-2 w-7 h-7 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 text-white text-xs font-bold flex items-center justify-center shadow-lg border-2 border-white/30">
                              {index + 1}
                            </div>
                            {/* Click to expand hint */}
                            <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                              <p className="text-white text-xs font-medium text-center">Click to expand</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Show submitted audio proof */}
                  {signedProofUrls.length > 0 && b.proof_type === "audio" && (
                    <div className="mt-3">
                      <Label>Your Submitted Audio Proof:</Label>
                      <div className="mt-2 p-4 bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-950/20 dark:to-indigo-950/20 border-2 border-purple-300 dark:border-purple-700 rounded-lg">
                        <div className="flex items-center gap-2 mb-3">
                          <Mic className="h-5 w-5 text-purple-600" />
                          <h4 className="font-bold text-purple-900 dark:text-purple-100">Audio Recording</h4>
                        </div>
                        
                        {signedProofUrls.map((url, index) => (
                          <AudioPlayer
                            key={index}
                            audioUrl={url}
                            autoPlay={false}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                );
              })() : (
                // NO PROOF SUBMITTED YET - Show submission form
                <>
                  <h3 className="font-display text-lg font-bold">Submit your proof</h3>
                  
                  {isImageProofType(proofType) && !photoSet && (
                    <div className="space-y-3">
                      <div className="space-y-1">
                        <Label htmlFor="file">
                          <Upload className="h-4 w-4 inline mr-1" />
                          Upload {proofType === "photo" ? "Photo" : "Screenshot"}
                        </Label>
                        <Input 
                          id="file" 
                          type="file" 
                          accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
                          multiple
                          onChange={handleFileSelect}
                        />
                        <p className="text-xs text-muted-foreground">
                          ✅ Accepted: JPG, PNG, WebP, GIF, PDF, Word, Excel, Text files
                          <br />
                          📏 Max size: 50MB per file
                        </p>
                      </div>
                      
                      {/* File Previews */}
                      {filePreviews.length > 0 && (
                        <div className="space-y-2 mt-4">
                          <Label>Selected Files ({files.length})</Label>
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                            {filePreviews.map((preview, index) => (
                              <div key={index} className="relative group">
                                {preview.startsWith('http') || preview.startsWith('blob:') ? (
                                  <div className="aspect-square rounded-lg overflow-hidden border-2 border-emerald-200 dark:border-emerald-800">
                                    <img 
                                      src={preview} 
                                      alt={`Preview ${index + 1}`}
                                      className="w-full h-full object-cover"
                                    />
                                  </div>
                                ) : preview === 'pdf' ? (
                                  <div className="aspect-square rounded-lg overflow-hidden border-2 border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/20 flex flex-col items-center justify-center p-4">
                                    <FileText className="h-12 w-12 text-red-600 dark:text-red-400 mb-2" />
                                    <p className="text-xs text-center text-red-700 dark:text-red-300 font-medium">
                                      PDF Document
                                    </p>
                                  </div>
                                ) : (
                                  <div className="aspect-square rounded-lg overflow-hidden border-2 border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/20 flex flex-col items-center justify-center p-4">
                                    <FileText className="h-12 w-12 text-blue-600 dark:text-blue-400 mb-2" />
                                    <p className="text-xs text-center text-blue-700 dark:text-blue-300 font-medium">
                                      {files[index]?.name.split('.').pop()?.toUpperCase()} File
                                    </p>
                                  </div>
                                )}
                                
                                <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-xs p-2 truncate">
                                  {files[index]?.name}
                                </div>
                                
                                <button
                                  onClick={() => {
                                    const newFiles = files.filter((_, i) => i !== index);
                                    setFiles(newFiles);
                                    const newPreviews = filePreviews.filter((_, i) => i !== index);
                                    if (preview.startsWith('blob:')) URL.revokeObjectURL(preview);
                                    setFilePreviews(newPreviews);
                                  }}
                                  className="absolute top-1 right-1 bg-red-500 hover:bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  {isImageProofType(proofType) && photoSet && (
                    <div className="space-y-3">
                      <div className="space-y-1">
                        <Label>
                          <Upload className="h-4 w-4 inline mr-1" />
                          Upload {photoSet.min} labeled photos
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          Each slot below requires exactly one photo.
                        </p>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        {photoSet.labels.map((label, index) => (
                          <div key={index} className="p-3 rounded-lg border-2 border-dashed border-[hsl(var(--paper-edge))] bg-background/50 space-y-2">
                            <Label className="text-xs font-bold flex items-center gap-1.5">
                              <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/20 text-primary text-[10px] font-bold flex items-center justify-center">{index + 1}</span>
                              {label}
                            </Label>
                            {photoSetPreviews[index] ? (
                              <div className="relative group aspect-square rounded-lg overflow-hidden border-2 border-emerald-200 dark:border-emerald-800">
                                <img
                                  src={photoSetPreviews[index] as string}
                                  alt={label}
                                  className="w-full h-full object-cover"
                                />
                                <button
                                  type="button"
                                  onClick={() => removePhotoSetSlot(index)}
                                  className="absolute top-1 right-1 bg-red-500 hover:bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                  title="Remove photo"
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </div>
                            ) : (
                              <label className="block cursor-pointer">
                                <Input
                                  type="file"
                                  accept="image/*"
                                  className="hidden"
                                  onChange={(e) => handlePhotoSetSlot(index, e)}
                                />
                                <div className="aspect-square rounded-lg border-2 border-dashed border-[hsl(var(--paper-edge))] flex flex-col items-center justify-center text-muted-foreground hover:border-primary hover:text-primary transition-colors">
                                  <Upload className="h-8 w-8 mb-1" />
                                  <span className="text-xs font-medium">Add photo</span>
                                </div>
                              </label>
                            )}
                          </div>
                        ))}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        ✅ Accepted: JPG, PNG, WebP, GIF · 📏 Max size: 50MB per photo
                      </p>
                    </div>
                  )}
                  {proofType === "video" && (
                    <div className="space-y-3">
                      <Label htmlFor="file-video">
                        <Upload className="h-4 w-4 inline mr-1" />
                        Upload video proof
                      </Label>
                      <Input id="file-video" type="file" accept="video/*" multiple onChange={handleFileSelect} />
                      <p className="text-xs text-muted-foreground">
                        Up to {MAX_VIDEO_FILES} videos, max {MAX_VIDEO_DURATION_SECONDS} seconds each
                      </p>
                      {filePreviews.length > 0 && (
                        <div className="grid gap-2">
                          {filePreviews.map((preview, index) => (
                            <video key={index} src={preview} controls className="w-full max-h-48 rounded border" />
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  {(proofType === "document" || proofType === "pdf") && (
                    <div className="space-y-3">
                      <Label htmlFor="file-doc">
                        <Upload className="h-4 w-4 inline mr-1" />
                        Upload PDF or document
                      </Label>
                      <Input id="file-doc" type="file" accept="application/pdf,.doc,.docx" multiple onChange={handleFileSelect} />
                    </div>
                  )}
                  {proofType === "link" && (
                    <div className="space-y-1">
                      <Label htmlFor="url">Proof URL</Label>
                      <Input id="url" type="url" placeholder="https://..." value={proofText} onChange={(e) => setProofText(e.target.value)} maxLength={500} />
                    </div>
                  )}
                  {proofType === "text" && (
                    <div className="space-y-1">
                      <Label htmlFor="txt">Your answer</Label>
                      <Textarea id="txt" rows={4} value={proofText} onChange={(e) => setProofText(e.target.value)} maxLength={1000} />
                    </div>
                  )}
                  {proofType === "audio" && (
                    <div className="space-y-4">
                      <div className="flex items-center gap-2 mb-3">
                        <Mic className="h-5 w-5 text-primary" />
                        <h3 className="font-bold text-lg">Record Audio Proof</h3>
                      </div>
                      
                      <AudioRecorder
                        onRecordingComplete={(file) => {
                          setFiles([file]);
                          const audioUrl = URL.createObjectURL(file);
                          setFilePreviews([audioUrl]);
                        }}
                        maxDurationSeconds={120}
                        maxSizeMB={10}
                      />
                      
                      <p className="text-xs text-muted-foreground">
                        💡 Record a clear audio proof (max 2 minutes, 10MB)
                      </p>
                    </div>
                  )}
                  <Button className="w-full" onClick={submitProof} disabled={!canSubmitProofForm}>
                    {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    {busy ? "Validating & Submitting..." : "Submit Proof"}
                  </Button>
                  {proofType === "video" && !proofFilesValid && (
                    <p className="text-xs text-red-600 mt-2">
                      Each video must be {MAX_VIDEO_DURATION_SECONDS} seconds or less. Submit is disabled until fixed.
                    </p>
                  )}
                </>
              )}
            </div>
          )}
  
        </div>

        <ProofRejectionDialog
          open={!!rejectDialogClaimId}
          onOpenChange={(open) => !open && setRejectDialogClaimId(null)}
          onConfirm={handleConfirmRejectProof}
          busy={busy}
        />
  
        {/* Chat Section - Only for participants */}
        {(isRequester || isHunter || claimRequests.some(c => c.hunter_id === user?.id && (c.status === "accepted" || c.status === "pending"))) && user && (
          <div className="poster p-6 pt-8">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                  <MessageSquare className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-display text-lg font-bold">Messages</h3>
                  <p className="text-xs text-muted-foreground">Communicate securely</p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowChat(!showChat)}
                className="rounded-lg"
              >
                {showChat ? "Hide" : "Show"}
              </Button>
            </div>
            {showChat && (
              <div className="space-y-3">
                {/* Hunter selector for bounty posters */}
                {isRequester && claimRequests.filter(c => c.status === "accepted" || c.status === "pending").length > 0 && (
                  <div className="flex items-center gap-2">
                    <Label className="text-sm font-semibold">Chat with:</Label>
                    <select
                      value={selectedChatHunter || claimRequests[0]?.hunter_id || ""}
                      onChange={(e) => setSelectedChatHunter(e.target.value)}
                      className="flex-1 px-3 py-2 border-2 border-[hsl(var(--paper-edge))] rounded-lg bg-[hsl(var(--paper))] dark:bg-[hsl(25_25%_25%)] text-foreground focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all"
                    >
                      {claimRequests
                        .filter(c => c.status === "accepted" || c.status === "pending")
                        .map(claim => (
                          <option key={claim.hunter_id} value={claim.hunter_id}>
                            {claim.hunter_name || "Anonymous Hunter"} ({claim.status})
                          </option>
                        ))}
                    </select>
                  </div>
                )}
                <BountyChat
                  bountyId={b.id}
                  currentUserId={user.id}
                  receiverId={
                    isRequester 
                      ? (selectedChatHunter || claimRequests.find(c => c.status === "accepted")?.hunter_id || claimRequests[0]?.hunter_id || "")
                      : b.requester_id
                  }
                  receiverName={
                    isRequester
                      ? (claimRequests.find(c => c.hunter_id === (selectedChatHunter || claimRequests.find(c => c.status === "accepted")?.hunter_id || claimRequests[0]?.hunter_id))?.hunter_name || "Hunter")
                      : (b as any).requester_name || "Bounty Owner"
                  }
                />
              </div>
            )}
          </div>
        )}
      </main>
      
      {/* Extension Dialog */}
      {b && user && (
        <BountyExtensionDialog
          isOpen={showExtensionDialog}
          onClose={() => setShowExtensionDialog(false)}
          bountyId={b.id}
          userId={user.id}
          currentExpiresAt={b.expires_at}
          onSuccess={handleExtensionSuccess}
        />
      )}

      {/* Photo Lightbox */}
      {photoLightbox.isOpen && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
          onClick={closeLightbox}
        >
          {/* Close button */}
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 z-50 p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
          >
            <X className="h-6 w-6 text-white" />
          </button>

          {/* Previous button */}
          {photoLightbox.photos.length > 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); navigateLightbox('prev'); }}
              className="absolute left-4 z-50 p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
            >
              <ChevronLeft className="h-8 w-8 text-white" />
            </button>
          )}

          {/* Next button */}
          {photoLightbox.photos.length > 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); navigateLightbox('next'); }}
              className="absolute right-4 z-50 p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
            >
              <ChevronRight className="h-8 w-8 text-white" />
            </button>
          )}

          {/* Image container */}
          <div 
            className="relative max-w-[90vw] max-h-[90vh] flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={photoLightbox.photos[photoLightbox.currentIndex]}
              alt={`Photo ${photoLightbox.currentIndex + 1}`}
              className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
            />
            
            {/* Photo counter */}
            {photoLightbox.photos.length > 1 && (
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/60 text-white px-4 py-2 rounded-full text-sm">
                {photoLightbox.currentIndex + 1} / {photoLightbox.photos.length}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Cancel Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <Trash2 className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
              <AlertDialogTitle className="text-xl">Cancel Bounty?</AlertDialogTitle>
            </div>
            <AlertDialogDescription className="text-base">
              This closes the bounty and refunds the unclaimed reward to your wallet. The service fee is not
              refunded, and any hunter still working on it will be released. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 sm:gap-0">
            <AlertDialogCancel className="flex-1">Keep Bounty</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteBounty}
              className="flex-1 bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              Yes, Cancel &amp; Refund
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {/* GLOBAL Proof Viewer Dialog - Available for BOTH hunters and owners */}
      <Dialog open={!!proofViewerUrl} onOpenChange={(open) => !open && setProofViewerUrl(null)}>
        <DialogContent className="max-w-7xl w-full h-[90vh] p-0 bg-transparent border-none overflow-hidden">
          {/* Close button */}
          <button
            onClick={() => setProofViewerUrl(null)}
            className="absolute top-4 right-4 z-[60] p-2 sm:p-3 bg-black/50 hover:bg-black/70 rounded-full transition-colors shadow-lg"
            aria-label="Close proof viewer"
          >
            <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          
          {/* Image Viewer */}
          {proofViewerType === 'image' && proofViewerUrl && (
            <div className="w-full h-full flex items-center justify-center bg-black/90">
              <img 
                src={proofViewerUrl} 
                alt="Proof" 
                className="max-w-full max-h-[90vh] object-contain"
              />
            </div>
          )}
          
          {/* PDF Viewer */}
          {proofViewerType === 'pdf' && proofViewerUrl && (
            <iframe
              src={proofViewerUrl}
              className="w-full h-[90vh] bg-white"
              title="PDF Proof"
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Bounty Wall Publish Dialog (offered after proof approval) */}
      <WallPublishDialog
        open={!!wallDialogClaim}
        onOpenChange={(open) => !open && setWallDialogClaim(null)}
        claim={wallDialogClaim}
        bounty={{
          title: b.title,
          description: (b as any).description,
          country: (b as any).country,
          location: (b as any).location,
          proof_requirements: (b as any).proof_requirements,
        }}
        onPublished={() => {
          // Refresh badges after publish
          setTimeout(() => {
            loadClaimRequests();
            wallApi.getWallEntryByBountyId(b.id).then(({ data }) => {
              if (data) setWallEntriesByClaim({ [data.claim_id]: { slug: data.slug } });
            });
          }, 500);
        }}
      />
    </div>
  );
};

export default Detail;
